/* eslint-disable react/no-unescaped-entities */
import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import TourneeLanding from "./tournee/TourneeLanding";
import TourneeVille from "./tournee/TourneeVille";
import TourneeAdmin from "./tournee/TourneeAdmin";
import axios from "axios";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import {
  Sun, Moon, Calendar, CalendarDays, Heart, BookOpen,
  MessageCircle, Youtube, Music, FileText, Users,
  Send, ChevronDown, Instagram, Facebook, ArrowRight,
  GraduationCap, Star, ExternalLink, X, Lock, Trash2, Save,
  Shield, CheckCircle2, XCircle, Clock, Quote, Compass, Sparkles, CheckCircle,
  Plus, Radio, Layers, Globe, MapPinned
} from "lucide-react";
import "@/App.css";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;

// ── Réglages centralisés (modifiés ici, utilisés partout) ──
// Prix unique de la séance individuelle à distance (page /soins)
const SESSION_PRICE_EUR = 30;
// Tao des 4C : lien externe ACTIVÉ (demande de Denis — les boutons pointent vers taodes4c.fr).
// Pour désactiver : passer TAO4C_ENABLED à false (les boutons redeviennent des pastilles « Bientôt disponible »).
const TAO4C_ENABLED = true;
const TAO4C_URL = "https://taodes4c.fr";
const ENERGETIQUE_URL = "https://energeticienconscience.fr";
const WEB3FORMS_URL = "https://api.web3forms.com/submit";

// Send an email notification via Web3Forms (browser-side, free plan).
// Web3Forms doesn't return CORS headers when called from the Emergent preview
// domain. We use `mode: 'no-cors'` so the POST still reaches the server (the
// email gets delivered) even though we cannot read the response from JS.
// In production on a registered domain CORS works normally.
async function sendWeb3Form({ subject, fromName, message, replyTo }) {
  if (!WEB3FORMS_KEY) return false;
  try {
    const fd = new FormData();
    fd.append("access_key", WEB3FORMS_KEY);
    fd.append("subject", subject);
    fd.append("from_name", fromName);
    fd.append("message", message);
    if (replyTo) fd.append("replyto", replyTo);
    // Fire-and-forget: with no-cors the response is opaque (status 0) but the
    // request is dispatched and the email is delivered by Web3Forms.
    await fetch(WEB3FORMS_URL, { method: "POST", body: fd, mode: "no-cors" });
    return true;
  } catch (e) {
    console.error("Web3Forms error:", e?.message || e);
    return false;
  }
}

// ============== THEME HOOK ==============
function useTheme() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "light";
    const saved = localStorage.getItem("theme");
    if (saved) return saved;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    localStorage.setItem("theme", theme);
  }, [theme]);

  // Listen to system changes only if user hasn't explicitly chosen
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => {
      if (!localStorage.getItem("theme-locked")) {
        setTheme(e.matches ? "dark" : "light");
      }
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return [theme, (t) => { localStorage.setItem("theme-locked", "1"); setTheme(t); }];
}

// ============== NAV ==============
// Each item is either an in-page anchor (id) or a full page route (to).
// Items with `children` render as a dropdown (desktop) / indented group (mobile).
const NAV_ITEMS = [
  { to: "/", label: "Accueil" },
  { to: "/soins", label: "Soins" },
  { to: "/energetique-de-la-conscience", label: "Énergétique de la Conscience" },
  { to: "/formation", label: "Formations" },
  { to: "/tao-des-4c", label: "Tao des 4C" },
  { to: "/tournee", label: "La Tournée" },
  { id: "presentation", label: "Mon parcours" },
  { to: "/contact", label: "Contact" },
];

function buildHref(item, pathname) {
  if (item.to) return item.to;
  // Anchor link: works on /, prefixed with / from other routes so React Router navigates first
  return pathname === "/" ? `#${item.id}` : `/#${item.id}`;
}

function Nav({ theme, setTheme, onAdminClick }) {
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // key of the open dropdown
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the dropdown on route change or Escape
  useEffect(() => { setOpenMenu(null); }, [pathname]);
  useEffect(() => {
    if (!openMenu) return;
    const onKey = (e) => { if (e.key === "Escape") setOpenMenu(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openMenu]);

  const slugify = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  const testKey = (item) => {
    if (item.external) return slugify(item.label);
    return item.to ? item.to.split("#")[0].replace(/\//g, "") || "home" : item.id;
  };
  const childKey = (child) => (child.id || child.to || "").replace(/[/#]/g, "-").replace(/^-+|-+$/g, "");

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-ivory/85 dark:bg-night/85 border-b border-beige/60 dark:border-charcoal/60"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 h-16 sm:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="font-serif text-xl sm:text-2xl tracking-wide text-forest dark:text-whisper"
        >
          Le guérisseur
          <span className="text-gold ml-1">·</span>
        </Link>

        <ul className="hidden lg:flex items-center gap-4 xl:gap-5 text-[11px] xl:text-[12px] tracking-[0.06em] uppercase text-stone dark:text-mist">
          {NAV_ITEMS.map((item) => {
            const key = testKey(item);
            if (item.children) {
              const isOpen = openMenu === key;
              return (
                <li
                  key={key}
                  className="relative"
                  onMouseLeave={() => setOpenMenu(null)}
                >
                  <button
                    data-testid={`nav-${key}`}
                    aria-haspopup="true"
                    aria-expanded={isOpen}
                    onClick={() => setOpenMenu(isOpen ? null : key)}
                    className={`link-underline inline-flex items-center gap-1.5 uppercase tracking-[0.06em] whitespace-nowrap transition-colors ${isOpen ? "text-forest dark:text-gold" : "hover:text-forest dark:hover:text-whisper"}`}
                  >
                    {item.label}
                    <ChevronDown size={12} className={`transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        data-testid={`nav-menu-${key}`}
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                        className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50"
                      >
                        <ul className="min-w-[220px] rounded-xl border border-beige dark:border-charcoal bg-ivory/95 dark:bg-night/95 backdrop-blur-xl shadow-xl shadow-ink/10 py-2">
                          {item.children.map((child) => {
                            const ck = childKey(child);
                            return (
                              <li key={ck}>
                                <Link
                                  data-testid={`nav-sub-${ck}`}
                                  to={child.to}
                                  onClick={() => setOpenMenu(null)}
                                  className="block px-5 py-2.5 text-[12px] tracking-[0.08em] uppercase text-stone dark:text-mist hover:text-forest dark:hover:text-gold hover:bg-sand/70 dark:hover:bg-onyx/70 transition-colors"
                                >
                                  {child.label}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>
              );
            }
            const href = buildHref(item, pathname);
            const isActive = item.to && !item.external && pathname === item.to;
            return (
              <li key={key}>
                <a
                  data-testid={`nav-${key}`}
                  href={href}
                  {...(item.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  className={`link-underline whitespace-nowrap transition-colors ${isActive ? "text-forest dark:text-gold" : "hover:text-forest dark:hover:text-whisper"}`}
                >
                  {item.label}
                </a>
              </li>
            );
          })}
        </ul>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-cta-rdv"
            href="https://tidycal.com/denistran"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:inline-flex items-center gap-1.5 bg-gold text-night hover:bg-gold-bright rounded-full px-4 py-2 text-[11px] tracking-[0.14em] uppercase font-medium transition-colors whitespace-nowrap"
          >
            <span className="xl:hidden">Rendez-vous</span>
            <span className="hidden xl:inline">Prendre rendez-vous</span>
          </a>
          <button
            data-testid="admin-open-btn"
            aria-label="Ouvrir le dashboard administrateur"
            onClick={onAdminClick}
            className="h-10 w-10 rounded-full border border-beige dark:border-charcoal hover:border-forest dark:hover:border-gold flex items-center justify-center transition-colors text-stone dark:text-mist hover:text-forest dark:hover:text-gold"
            title="Admin"
          >
            <Shield size={15} strokeWidth={1.5} />
          </button>
          <Link
            data-testid="admin-tournee-btn"
            to="/tournee/admin"
            aria-label="Ouvrir le back-office de la Tournée"
            className="h-10 w-10 rounded-full border border-beige dark:border-charcoal hover:border-forest dark:hover:border-gold flex items-center justify-center transition-colors text-stone dark:text-mist hover:text-forest dark:hover:text-gold"
            title="Admin Tournée"
          >
            <MapPinned size={15} strokeWidth={1.5} />
          </Link>

          <button
            data-testid="theme-toggle"
            aria-label="Basculer entre le mode clair et le mode sombre"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="h-10 w-10 rounded-full border border-beige dark:border-charcoal hover:border-forest dark:hover:border-gold flex items-center justify-center transition-colors"
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          <button
            data-testid="nav-menu-toggle"
            aria-label="Ouvrir le menu"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden h-10 w-10 rounded-full border border-beige dark:border-charcoal flex items-center justify-center"
          >
            <span className="block w-4 h-[1.5px] bg-current relative">
              <span className="absolute left-0 -top-[5px] block w-4 h-[1.5px] bg-current" />
              <span className="absolute left-0 top-[5px] block w-4 h-[1.5px] bg-current" />
            </span>
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="nav-mobile-menu"
          data-lenis-prevent
          className="lg:hidden bg-ivory/95 dark:bg-night/95 backdrop-blur-xl border-t border-beige dark:border-charcoal max-h-[calc(100vh-4rem)] overflow-y-auto"
        >
          <ul className="px-6 py-6 space-y-3 text-sm tracking-wide uppercase">
            {NAV_ITEMS.map((item) => {
              const key = testKey(item);
              if (item.children) {
                return (
                  <li key={key}>
                    <Link
                      data-testid={`nav-mobile-${key}`}
                      to={item.to || buildHref(item, pathname)}
                      onClick={() => setOpen(false)}
                      className="block pt-2 pb-1 text-[10px] tracking-[0.3em] uppercase text-gold/80 dark:text-gold/70 hover:text-gold"
                    >
                      {item.label}
                    </Link>
                    <ul className="pl-4 border-l border-beige dark:border-charcoal space-y-1">
                      {item.children.map((child) => {
                        const ck = childKey(child);
                        return (
                          <li key={ck}>
                            <Link
                              data-testid={`nav-mobile-sub-${ck}`}
                              onClick={() => setOpen(false)}
                              to={child.to || buildHref(child, pathname)}
                              className="block py-1.5 text-stone dark:text-mist hover:text-forest dark:hover:text-whisper"
                            >
                              {child.label}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                );
              }
              const href = buildHref(item, pathname);
              return (
                <li key={key}>
                  <a
                    data-testid={`nav-mobile-${key}`}
                    onClick={() => setOpen(false)}
                    href={href}
                    {...(item.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    className="block py-2 text-stone dark:text-mist hover:text-forest dark:hover:text-whisper"
                  >
                    {item.label}
                  </a>
                </li>
              );
            })}
          </ul>
          <div className="px-6 pb-8 pt-2">
            <a
              data-testid="nav-mobile-cta-rdv"
              href="https://tidycal.com/denistran"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="inline-flex w-full items-center justify-center gap-2 bg-gold text-night hover:bg-gold-bright rounded-full px-6 py-3.5 text-[11px] tracking-[0.16em] uppercase font-medium transition-colors"
            >
              Prendre rendez-vous
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

// ============== BUTTONS ==============
const btnPrimary =
  "inline-flex items-center justify-center gap-2 bg-forest dark:bg-forest-light text-ivory hover:bg-forest-hover dark:hover:bg-forest-lighter rounded-full px-7 py-3.5 transition-all duration-300 text-[12px] tracking-[0.16em] uppercase hover:-translate-y-0.5";
const btnSecondary =
  "inline-flex items-center justify-center gap-2 bg-transparent border border-forest text-forest dark:border-forest-light dark:text-whisper hover:bg-forest/5 dark:hover:bg-forest-light/10 rounded-full px-7 py-3.5 transition-all duration-300 text-[12px] tracking-[0.16em] uppercase hover:-translate-y-0.5";

// ============== HERO ==============
const PORTRAIT_URL = "https://customer-assets.emergentagent.com/job_qi-gateway/artifacts/4vkakzpb_Le%20Guerisseur.jpeg";

function Hero() {
  // Preuve sociale réelle : note moyenne et nombre issus des avis approuvés (API publique).
  // Rien n'est affiché si aucun témoignage n'existe.
  const [proof, setProof] = useState(null);
  useEffect(() => {
    axios.get(`${API}/reviews`)
      .then(({ data }) => { const s = data?.stats; if (s && s.count > 0) setProof(s); })
      .catch(() => { /* silent */ });
  }, []);
  return (
    <section
      id="accueil"
      data-testid="section-hero"
      className="relative min-h-[100svh] flex items-center pt-28 pb-20 overflow-hidden"
    >
      {/* Misty mountain backdrop with veil — atmospheric, spiritual */}
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="https://images.unsplash.com/photo-1773975345930-e2ab9d33b8ae?auto=format&fit=crop&q=80&w=2400"
          alt="Montagnes brumeuses avec temple — ambiance spirituelle"
          className="ken-burns w-full h-full object-cover opacity-60 dark:opacity-70"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ivory/95 via-ivory/70 to-ivory/30 dark:from-night/95 dark:via-night/78 dark:to-night/40" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.10),transparent_60%)] dark:bg-[radial-gradient(ellipse_at_top_right,rgba(229,193,88,0.12),transparent_60%)]" />
        <div className="absolute inset-x-0 bottom-0 h-px gold-divider opacity-60" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Text column */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="lg:col-span-7"
        >
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-12 bg-gold" />
            <span className="text-[11px] tracking-[0.3em] uppercase text-stone dark:text-mist">
              Denis Tran — Pratique · Accompagnement · Transmission
            </span>
          </div>

          <h1
            data-testid="hero-h1"
            className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-ink dark:text-whisper"
          >
            Le Guérisseur
          </h1>
          <p
            data-testid="hero-subtitle"
            className="mt-4 font-serif text-2xl sm:text-3xl text-forest dark:text-gold italic"
          >
            L'Énergétique de la Conscience
          </p>

          <p className="mt-8 text-base sm:text-lg leading-relaxed text-stone dark:text-mist max-w-2xl">
            Une approche énergétique développée par Denis Tran, inspirée du Qi Gong,
            de la Médecine Traditionnelle Chinoise, du Taoïsme et de plus de 25 ans
            d'expérience dans le massage et l'accompagnement.
          </p>
          <p data-testid="hero-mantra" className="mt-5 font-serif italic text-lg sm:text-xl text-forest dark:text-gold">
            Comprendre son corps. Éveiller sa conscience. Expérimenter autrement.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link data-testid="hero-cta-accompagnement" to="/soins" className={btnPrimary}>
              Recevoir un accompagnement <ArrowRight size={14} />
            </Link>
            <a data-testid="hero-cta-methode" href="#methode" className={btnSecondary}>
              Découvrir la méthode
            </a>
          </div>
          {proof && (
            <Link data-testid="hero-social-proof" to="/temoignages" className="mt-6 inline-flex items-center gap-2.5">
              <span className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={15} strokeWidth={1.5} className={i <= Math.round(proof.average) ? "text-gold fill-gold" : "text-stone/30 dark:text-mist/30"} />
                ))}
              </span>
              <span className="text-sm text-stone dark:text-mist">
                <span className="font-serif text-lg text-ink dark:text-whisper">{proof.average.toFixed(1).replace(".", ",")}/5</span>
                <span className="mx-1.5">·</span>
                {proof.count} témoignage{proof.count > 1 ? "s" : ""}
              </span>
            </Link>
          )}
          <p data-testid="hero-legal-note" className="mt-4 text-xs italic text-stone/80 dark:text-mist/80">
            « Approche complémentaire et non médicale. »
          </p>
        </motion.div>

        {/* Portrait column */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: "easeOut", delay: 0.15 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative mx-auto max-w-md lg:max-w-none">
            {/* Gold ring */}
            <div className="absolute -inset-3 rounded-[2rem] border border-gold/40 dark:border-gold/30 pointer-events-none" />
            {/* Soft halo */}
            <div className="absolute -inset-10 bg-gold/10 dark:bg-gold/15 blur-3xl rounded-full pointer-events-none" />
            <div className="relative rounded-[1.75rem] overflow-hidden border border-beige dark:border-charcoal bg-black shadow-2xl shadow-black/20 dark:shadow-black/60">
              <img
                data-testid="hero-portrait"
                src={PORTRAIT_URL}
                alt="Denis Tran, Le guérisseur — portrait illustré"
                className="w-full h-auto object-cover"
                loading="eager"
              />
              <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/80 to-transparent">
                <p className="font-serif text-ivory text-lg sm:text-xl">Denis Tran</p>
                <p className="text-mist text-xs tracking-[0.2em] uppercase mt-1">Fondateur · Le Guérisseur</p>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden sm:flex items-center gap-2 text-stone dark:text-mist text-xs tracking-[0.2em] uppercase">
          <ChevronDown size={14} className="animate-bounce" />
          Faites défiler
        </div>
      </div>
    </section>
  );
}

// ============== CHAT WIDGET (myclone AI assistant) ==============
const CHAT_URL = "https://app.myclone.is/leguerisseur";

function ChatWidget({ open, onClose }) {
  // Lock body scroll when modal opens
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  if (!open) return null;
  return (
    <div data-testid="chat-modal" data-lenis-prevent className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6">
      <div className="w-full sm:max-w-md h-[88vh] sm:h-[80vh] bg-ivory/85 dark:bg-night/85 rounded-t-2xl sm:rounded-2xl border border-beige dark:border-charcoal overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-beige dark:border-charcoal bg-sand/60 dark:bg-onyx/60">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full overflow-hidden border border-gold/40 bg-black shrink-0">
              <img src={PORTRAIT_URL} alt="" className="w-full h-full object-cover object-top" />
            </div>
            <div>
              <p className="font-serif text-base text-ink dark:text-whisper leading-tight">Denis Tran</p>
              <p className="text-[10px] tracking-[0.18em] uppercase text-stone dark:text-mist">Assistant — IA spirituelle</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <a
              data-testid="chat-open-new-tab"
              href={CHAT_URL}
              target="_blank" rel="noopener noreferrer"
              aria-label="Ouvrir le chat en plein écran"
              title="Plein écran"
              className="h-8 w-8 rounded-full hover:bg-beige dark:hover:bg-charcoal flex items-center justify-center text-stone dark:text-mist"
            >
              <ExternalLink size={14} />
            </a>
            <button
              data-testid="chat-close"
              aria-label="Fermer le chat"
              onClick={onClose}
              className="h-8 w-8 rounded-full hover:bg-beige dark:hover:bg-charcoal flex items-center justify-center text-stone dark:text-mist"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        <iframe
          data-testid="chat-iframe"
          src={CHAT_URL}
          title="Discuter avec Le guérisseur"
          className="flex-1 w-full border-0 bg-ivory/85 dark:bg-night/85"
          allow="microphone; clipboard-write"
        />
      </div>
    </div>
  );
}

function ChatFab({ onClick }) {
  return (
    <button
      data-testid="chat-fab"
      onClick={onClick}
      aria-label="Discuter avec Le guérisseur"
      className="fixed bottom-24 sm:bottom-28 right-4 sm:right-6 z-40 group"
    >
      <span className="absolute -inset-1 bg-gold/40 rounded-full blur-md opacity-60 group-hover:opacity-100 transition-opacity" />
      <span className="relative flex items-center gap-2 bg-forest text-ivory dark:bg-gold dark:text-night rounded-full pl-3 pr-5 py-3 shadow-lg shadow-black/20 hover:-translate-y-0.5 transition-transform">
        <span className="relative h-9 w-9 rounded-full overflow-hidden bg-black border border-gold/40 shrink-0">
          <img src={PORTRAIT_URL} alt="" className="w-full h-full object-cover object-top" />
          <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-400 border-2 border-forest dark:border-gold" />
        </span>
        <span className="hidden sm:inline text-[11px] tracking-[0.16em] uppercase font-medium">Discuter</span>
      </span>
    </button>
  );
}

// ============== EVENT BANNER (rotating, 3 slots) ==============

// Returns a human-friendly French countdown like "dans 2 jours", "dans 3 h", "dans 12 min",
// "maintenant", "il y a X jours", or null when no ISO datetime is provided.
function formatCountdown(iso) {
  if (!iso) return null;
  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) return null;
  const diffMs = target.getTime() - Date.now();
  const past = diffMs < 0;
  const abs = Math.abs(diffMs);
  const min = Math.round(abs / 60000);
  const hr = Math.round(abs / 3600000);
  const day = Math.round(abs / 86400000);

  let label;
  if (min < 1) label = "maintenant";
  else if (min < 60) label = `${min} min`;
  else if (hr < 24) label = `${hr} h`;
  else if (day < 60) label = `${day} jour${day > 1 ? "s" : ""}`;
  else {
    // Beyond ~2 months, show the formatted date instead
    return target.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  }
  if (label === "maintenant") return "C'est maintenant";
  return past ? `il y a ${label}` : `dans ${label}`;
}

function EventBanner() {
  const [events, setEvents] = useState([]);
  const [index, setIndex] = useState(0);
  const [, forceTick] = useState(0); // re-render every minute to refresh countdowns
  const [dismissed, setDismissed] = useState(
    typeof window !== "undefined" && sessionStorage.getItem("event-banner-dismissed") === "1"
  );

  useEffect(() => {
    let cancel = false;
    // Admin banners + auto-injected "next formation session" banner (with countdown)
    Promise.allSettled([
      axios.get(`${API}/events/upcoming`),
      axios.get(`${API}/formation-dates`),
    ]).then(([evRes, fdRes]) => {
      if (cancel) return;
      const adminEvents = evRes.status === "fulfilled" ? (evRes.value.data?.events || []) : [];
      const dates = fdRes.status === "fulfilled" ? (fdRes.value.data?.dates || []) : [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const next = dates
        .filter((d) => !Number.isNaN(new Date(`${d.start_date}T12:00:00`).getTime()) && new Date(`${d.start_date}T12:00:00`) >= today)
        .sort((a, b) => a.start_date.localeCompare(b.start_date))[0];
      const sessionEvent = next ? [{
        id: "next-formation-session",
        title: `Prochaine formation — ${next.label}`,
        event_at: `${next.start_date}T09:00:00`,
        date: formatSessionRange(next.start_date, next.end_date),
        url: "/formation#candidature",
        cta_label: "Candidater",
      }] : [];
      setEvents([...sessionEvent, ...adminEvents]);
    });
    return () => { cancel = true; };
  }, []);

  // Rotate every 6s when more than one event
  useEffect(() => {
    if (events.length <= 1) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % events.length), 6000);
    return () => clearInterval(id);
  }, [events.length]);

  // Tick every 60s to refresh countdowns
  useEffect(() => {
    const id = setInterval(() => forceTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  if (dismissed || events.length === 0) return null;
  const event = events[index % events.length];
  const countdown = formatCountdown(event.event_at);
  const dateLabel = countdown || event.date;

  return (
    <div
      data-testid="event-banner"
      className="fixed bottom-0 inset-x-0 z-40 px-3 pb-3 sm:pb-5 pointer-events-none"
    >
      <div className="pointer-events-auto max-w-4xl mx-auto bg-night/95 dark:bg-night/95 backdrop-blur-xl border border-gold/40 rounded-2xl sm:rounded-full shadow-2xl shadow-black/30 px-4 sm:px-6 py-3 flex items-center gap-3 sm:gap-5">
        <div className="hidden sm:flex h-9 w-9 shrink-0 rounded-full bg-gold/15 items-center justify-center">
          <CalendarDays size={16} className="text-gold" strokeWidth={1.5} />
        </div>
        <div key={event.id} className="flex-1 min-w-0 animate-fade-up">
          <p className="text-[10px] sm:text-[11px] tracking-[0.25em] uppercase text-gold/80">
            À la une {dateLabel ? `— ${dateLabel}` : ""}
          </p>
          <p
            data-testid="event-banner-title"
            className="text-ivory text-sm sm:text-base font-medium truncate"
          >
            {event.title}
          </p>
        </div>
        {event.url?.startsWith("/") ? (
          <Link
            data-testid="event-banner-cta"
            to={event.url}
            className="inline-flex items-center justify-center gap-1.5 bg-gold text-night hover:bg-gold-bright rounded-full px-4 sm:px-5 py-2 text-[11px] tracking-[0.16em] uppercase font-medium transition-colors whitespace-nowrap"
          >
            {event.cta_label || "Réserver"} <ArrowRight size={12} />
          </Link>
        ) : (
          <a
            data-testid="event-banner-cta"
            href={event.url}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1.5 bg-gold text-night hover:bg-gold-bright rounded-full px-4 sm:px-5 py-2 text-[11px] tracking-[0.16em] uppercase font-medium transition-colors whitespace-nowrap"
          >
            {event.cta_label || "Réserver"} <ArrowRight size={12} />
          </a>
        )}
        {events.length > 1 && (
          <div data-testid="event-banner-dots" className="hidden sm:flex items-center gap-1.5 mx-1">
            {events.map((_, i) => (
              <button
                key={i}
                data-testid={`event-banner-dot-${i}`}
                aria-label={`Aller à l'annonce ${i + 1}`}
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-all ${i === index ? "w-5 bg-gold" : "w-1.5 bg-mist/40 hover:bg-mist/70"}`}
              />
            ))}
          </div>
        )}
        <button
          data-testid="event-banner-close"
          aria-label="Fermer la bannière"
          onClick={() => {
            sessionStorage.setItem("event-banner-dismissed", "1");
            setDismissed(true);
          }}
          className="h-8 w-8 shrink-0 rounded-full hover:bg-charcoal/60 flex items-center justify-center text-mist"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

// ============== ADMIN PANEL (open via prop / ?admin=1 / admin button) ==============
function AdminPanel({ open, onClose }) {
  const [token, setToken] = useState(() => localStorage.getItem("admin-token") || "");
  const [tab, setTab] = useState("event"); // event | reviews
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  // Event slots state (3 rotating banners)
  const emptySlot = { title: "", date: "", event_at: "", url: "", cta_label: "Réserver", active: true };
  const [slots, setSlots] = useState({ 1: { ...emptySlot }, 2: { ...emptySlot }, 3: { ...emptySlot } });
  const [activeSlot, setActiveSlot] = useState(1);
  // Reviews state
  const [reviewFilter, setReviewFilter] = useState("pending");
  const [reviews, setReviews] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [reviewsLoading, setReviewsLoading] = useState(false);
  // Formation dates state
  const emptyDate = { label: "", start_date: "", end_date: "", location: "", status: "ouverte", active: true };
  const [dates, setDates] = useState([]);
  const [dateForm, setDateForm] = useState({ ...emptyDate });
  const [editingDateId, setEditingDateId] = useState(null);
  const [datesLoading, setDatesLoading] = useState(false);
  // YouTube featured video/playlist state
  const [ytInput, setYtInput] = useState("");
  const [ytCurrent, setYtCurrent] = useState("");
  const [ytLoading, setYtLoading] = useState(false);

  // Load all 3 event slots when panel opens
  useEffect(() => {
    if (!open || !token) return;
    axios.get(`${API}/admin/events`, { headers: { "X-Admin-Token": token } }).then(({ data }) => {
      const next = { 1: { ...emptySlot }, 2: { ...emptySlot }, 3: { ...emptySlot } };
      (data?.events || []).forEach((ev) => {
        if (ev.slot >= 1 && ev.slot <= 3) {
          next[ev.slot] = {
            title: ev.title || "",
            date: ev.date || "",
            event_at: ev.event_at ? new Date(ev.event_at).toISOString().slice(0, 16) : "",
            url: ev.url || "",
            cta_label: ev.cta_label || "Réserver",
            active: ev.active !== false,
          };
        }
      });
      setSlots(next);
    }).catch(() => {});
  }, [open, token]);

  const loadReviews = async () => {
    if (!token) return;
    setReviewsLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reviews`, {
        headers: { "X-Admin-Token": token },
        params: { status: reviewFilter },
      });
      setReviews(data.reviews || []);
      setCounts(data.counts || { pending: 0, approved: 0, rejected: 0 });
    } catch (e) {
      setStatus({ state: "err", msg: e?.response?.data?.detail || "Erreur de chargement." });
    } finally {
      setReviewsLoading(false);
    }
  };

  // Load reviews when tab changes or filter changes
  useEffect(() => {
    if (!open || tab !== "reviews") return;
    loadReviews();
  }, [open, tab, reviewFilter]);

  // ---- Formation dates ----
  const loadDates = async () => {
    if (!token) return;
    setDatesLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/formation-dates`, { headers: { "X-Admin-Token": token } });
      setDates(data.dates || []);
    } catch (e) {
      setStatus({ state: "err", msg: e?.response?.data?.detail || "Erreur de chargement." });
    } finally {
      setDatesLoading(false);
    }
  };

  // Load formation dates when the tab opens
  useEffect(() => {
    if (!open || tab !== "dates") return;
    loadDates();
  }, [open, tab]);

  const changeDate = (k) => (e) => setDateForm((f) => ({
    ...f,
    [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
  }));

  const saveDate = async () => {
    if (!token) { setStatus({ state: "err", msg: "Token requis." }); return; }
    if (!dateForm.label.trim() || !dateForm.start_date) {
      setStatus({ state: "err", msg: "Intitulé et date de début requis." });
      return;
    }
    setStatus({ state: "sending", msg: "Enregistrement de la session…" });
    try {
      localStorage.setItem("admin-token", token);
      const payload = {
        ...dateForm,
        label: dateForm.label.trim(),
        end_date: dateForm.end_date || null,
        location: dateForm.location.trim() || null,
      };
      if (editingDateId) {
        await axios.put(`${API}/admin/formation-dates/${editingDateId}`, payload, { headers: { "X-Admin-Token": token } });
      } else {
        await axios.post(`${API}/admin/formation-dates`, payload, { headers: { "X-Admin-Token": token } });
      }
      const wasEditing = Boolean(editingDateId);
      setDateForm({ ...emptyDate });
      setEditingDateId(null);
      await loadDates();
      setStatus({ state: "ok", msg: wasEditing ? "Session mise à jour." : "Session ajoutée — visible sur la page Formation." });
    } catch (e) {
      setStatus({ state: "err", msg: e?.response?.data?.detail || "Erreur." });
    }
  };

  const editDate = (d) => {
    setEditingDateId(d.id);
    setDateForm({
      label: d.label || "",
      start_date: d.start_date || "",
      end_date: d.end_date || "",
      location: d.location || "",
      status: d.status || "ouverte",
      active: d.active !== false,
    });
  };

  const deleteDate = async (id) => {
    if (!token) { setStatus({ state: "err", msg: "Token requis." }); return; }
    if (!window.confirm("Supprimer définitivement cette session ?")) return;
    try {
      await axios.delete(`${API}/admin/formation-dates/${id}`, { headers: { "X-Admin-Token": token } });
      if (editingDateId === id) { setEditingDateId(null); setDateForm({ ...emptyDate }); }
      await loadDates();
      setStatus({ state: "ok", msg: "Session supprimée." });
    } catch (e) {
      setStatus({ state: "err", msg: e?.response?.data?.detail || "Erreur." });
    }
  };

  // ---- YouTube featured video/playlist ----
  const loadYoutube = async () => {
    setYtLoading(true);
    try {
      const { data } = await axios.get(`${API}/settings/youtube`);
      setYtCurrent(data.embed_url || "");
      setYtInput(data.embed_url || "");
    } catch (e) { /* silent */ }
    finally { setYtLoading(false); }
  };

  useEffect(() => {
    if (!open || tab !== "video") return;
    loadYoutube();
  }, [open, tab]);

  const saveYoutube = async () => {
    if (!token) { setStatus({ state: "err", msg: "Token requis." }); return; }
    if (!ytInput.trim()) { setStatus({ state: "err", msg: "Collez le code iframe ou le lien YouTube." }); return; }
    setStatus({ state: "sending", msg: "Enregistrement de la vidéo…" });
    try {
      localStorage.setItem("admin-token", token);
      const { data } = await axios.put(`${API}/admin/settings/youtube`, { embed: ytInput.trim() }, { headers: { "X-Admin-Token": token } });
      setYtCurrent(data.embed_url);
      setYtInput(data.embed_url);
      setStatus({ state: "ok", msg: "Vidéo mise à jour sur les deux pages." });
    } catch (e) {
      setStatus({ state: "err", msg: e?.response?.data?.detail || "Erreur." });
    }
  };

  if (!open) return null;

  const changeSlot = (slotNum, key) => (e) => setSlots((s) => ({
    ...s,
    [slotNum]: { ...s[slotNum], [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value },
  }));

  const saveSlot = async (slotNum) => {
    if (!token) { setStatus({ state: "err", msg: "Token requis." }); return; }
    const data = slots[slotNum];
    if (!data.title || !data.url) { setStatus({ state: "err", msg: "Titre et URL requis." }); return; }
    setStatus({ state: "sending", msg: `Enregistrement bannière ${slotNum}…` });
    try {
      localStorage.setItem("admin-token", token);
      // Convert datetime-local (no timezone) to ISO with local TZ offset
      const payload = { ...data };
      if (payload.event_at) {
        const d = new Date(payload.event_at);
        if (!Number.isNaN(d.getTime())) payload.event_at = d.toISOString();
        else payload.event_at = null;
      } else {
        payload.event_at = null;
      }
      await axios.put(`${API}/admin/events/${slotNum}`, payload, { headers: { "X-Admin-Token": token } });
      setStatus({ state: "ok", msg: `Bannière ${slotNum} enregistrée. Rechargez la page pour voir la rotation.` });
    } catch (e) {
      setStatus({ state: "err", msg: e?.response?.data?.detail || "Erreur." });
    }
  };

  const clearSlot = async (slotNum) => {
    if (!token) { setStatus({ state: "err", msg: "Token requis." }); return; }
    setStatus({ state: "sending", msg: `Suppression bannière ${slotNum}…` });
    try {
      await axios.delete(`${API}/admin/events/${slotNum}`, { headers: { "X-Admin-Token": token } });
      setSlots((s) => ({ ...s, [slotNum]: { ...emptySlot } }));
      setStatus({ state: "ok", msg: `Bannière ${slotNum} retirée.` });
    } catch (e) {
      setStatus({ state: "err", msg: e?.response?.data?.detail || "Erreur." });
    }
  };

  const moderateReview = async (id, newStatus, category = null) => {
    if (!token) { setStatus({ state: "err", msg: "Token requis." }); return; }
    try {
      localStorage.setItem("admin-token", token);
      const payload = { status: newStatus };
      if (category) payload.category = category;
      await axios.patch(`${API}/admin/reviews/${id}`, payload, { headers: { "X-Admin-Token": token } });
      await loadReviews();
    } catch (e) {
      setStatus({ state: "err", msg: e?.response?.data?.detail || "Erreur." });
    }
  };

  const deleteReview = async (id) => {
    if (!token) { setStatus({ state: "err", msg: "Token requis." }); return; }
    if (!window.confirm("Supprimer définitivement cet avis ?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, { headers: { "X-Admin-Token": token } });
      await loadReviews();
    } catch (e) {
      setStatus({ state: "err", msg: e?.response?.data?.detail || "Erreur." });
    }
  };

  const inputCls = "w-full bg-sand/85 dark:bg-onyx/85 border border-beige dark:border-charcoal rounded-lg px-4 py-2.5 text-ink dark:text-whisper focus:outline-none focus:border-forest dark:focus:border-gold";

  return (
    <div data-testid="admin-panel" data-lenis-prevent className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-ivory/85 dark:bg-night/85 border border-beige dark:border-charcoal rounded-2xl p-6 sm:p-8 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Shield size={18} className="text-forest dark:text-gold" />
            <h3 className="font-serif text-xl text-ink dark:text-whisper">Dashboard admin</h3>
          </div>
          <button data-testid="admin-close" aria-label="Fermer" onClick={onClose} className="h-9 w-9 rounded-full hover:bg-sand dark:hover:bg-onyx flex items-center justify-center text-stone dark:text-mist">
            <X size={16} />
          </button>
        </div>

        {/* Token */}
        <div className="mb-5">
          <label className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-1.5">Token admin</label>
          <input
            data-testid="admin-token-input"
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="X-Admin-Token"
            className={inputCls}
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-beige dark:border-charcoal mb-6">
          <button
            data-testid="admin-tab-event"
            onClick={() => setTab("event")}
            className={`px-4 py-2.5 text-[11px] tracking-[0.16em] uppercase border-b-2 -mb-px transition-colors ${tab === "event" ? "border-forest dark:border-gold text-forest dark:text-gold" : "border-transparent text-stone dark:text-mist hover:text-ink dark:hover:text-whisper"}`}
          >
            Bannières {[1, 2, 3].filter(n => slots[n]?.title && slots[n]?.active).length > 0 && (
              <span className="ml-1 inline-flex items-center justify-center bg-gold text-night text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px]">
                {[1, 2, 3].filter(n => slots[n]?.title && slots[n]?.active).length}/3
              </span>
            )}
          </button>
          <button
            data-testid="admin-tab-reviews"
            onClick={() => setTab("reviews")}
            className={`px-4 py-2.5 text-[11px] tracking-[0.16em] uppercase border-b-2 -mb-px transition-colors ${tab === "reviews" ? "border-forest dark:border-gold text-forest dark:text-gold" : "border-transparent text-stone dark:text-mist hover:text-ink dark:hover:text-whisper"}`}
          >
            Avis {counts.pending > 0 && <span className="ml-1 inline-flex items-center justify-center bg-gold text-night text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px]">{counts.pending}</span>}
          </button>
          <button
            data-testid="admin-tab-dates"
            onClick={() => setTab("dates")}
            className={`px-4 py-2.5 text-[11px] tracking-[0.16em] uppercase border-b-2 -mb-px transition-colors ${tab === "dates" ? "border-forest dark:border-gold text-forest dark:text-gold" : "border-transparent text-stone dark:text-mist hover:text-ink dark:hover:text-whisper"}`}
          >
            Dates formation {dates.filter((d) => d.active).length > 0 && (
              <span className="ml-1 inline-flex items-center justify-center bg-gold text-night text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px]">
                {dates.filter((d) => d.active).length}
              </span>
            )}
          </button>
          <button
            data-testid="admin-tab-video"
            onClick={() => setTab("video")}
            className={`px-4 py-2.5 text-[11px] tracking-[0.16em] uppercase border-b-2 -mb-px transition-colors ${tab === "video" ? "border-forest dark:border-gold text-forest dark:text-gold" : "border-transparent text-stone dark:text-mist hover:text-ink dark:hover:text-whisper"}`}
          >
            Vidéo
          </button>
        </div>

        {/* TAB: EVENTS (3 rotating slots) */}
        {tab === "event" && (
          <div>
            <p className="text-xs text-stone dark:text-mist mb-5 leading-relaxed">
              Configurez jusqu'à <strong>3 bannières</strong> qui tourneront automatiquement toutes les 6 secondes en bas du site.
            </p>
            <div className="flex gap-2 mb-5">
              {[1, 2, 3].map((n) => {
                const isSet = slots[n]?.title && slots[n]?.active;
                return (
                  <button
                    key={n}
                    data-testid={`admin-slot-tab-${n}`}
                    onClick={() => setActiveSlot(n)}
                    className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-xs border transition-colors ${activeSlot === n ? "bg-forest text-ivory border-forest dark:bg-gold dark:text-night dark:border-gold" : "border-beige dark:border-charcoal text-stone dark:text-mist hover:border-forest dark:hover:border-gold"}`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${isSet ? "bg-emerald-500" : "bg-stone/40 dark:bg-mist/40"}`} />
                    Bannière {n}
                  </button>
                );
              })}
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-1.5">Titre</label>
                <input data-testid={`admin-title-${activeSlot}`} value={slots[activeSlot].title} onChange={changeSlot(activeSlot, "title")} placeholder="Ex. Soin collectif en live" className={inputCls} />
              </div>
              <div>
                <label className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-1.5">Date & heure (compte à rebours auto)</label>
                <input data-testid={`admin-eventat-${activeSlot}`} type="datetime-local" value={slots[activeSlot].event_at} onChange={changeSlot(activeSlot, "event_at")} className={inputCls} />
                <p className="text-[10px] text-stone/70 dark:text-mist/70 mt-1.5">Si renseigné, la bannière affichera automatiquement "dans 2 jours", "dans 3 h"…</p>
              </div>
              <div>
                <label className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-1.5">Texte de date (repli si pas de date/heure)</label>
                <input data-testid={`admin-date-${activeSlot}`} value={slots[activeSlot].date} onChange={changeSlot(activeSlot, "date")} placeholder="Ex. Samedi 14 décembre, 20h" className={inputCls} />
              </div>
              <div>
                <label className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-1.5">URL de réservation</label>
                <input data-testid={`admin-url-${activeSlot}`} type="url" value={slots[activeSlot].url} onChange={changeSlot(activeSlot, "url")} placeholder="https://..." className={inputCls} />
              </div>
              <div>
                <label className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-1.5">Libellé du bouton</label>
                <input data-testid={`admin-cta-${activeSlot}`} value={slots[activeSlot].cta_label} onChange={changeSlot(activeSlot, "cta_label")} className={inputCls} />
              </div>
              <label className="flex items-center gap-3 text-sm text-stone dark:text-mist">
                <input data-testid={`admin-active-${activeSlot}`} type="checkbox" checked={slots[activeSlot].active} onChange={changeSlot(activeSlot, "active")} className="h-4 w-4 accent-forest" />
                Bannière {activeSlot} active (incluse dans la rotation)
              </label>
              <div className="flex flex-wrap gap-3 pt-2">
                <button data-testid={`admin-save-${activeSlot}`} onClick={() => saveSlot(activeSlot)} className={btnPrimary}>
                  <Save size={14} /> Enregistrer la bannière {activeSlot}
                </button>
                <button data-testid={`admin-clear-${activeSlot}`} onClick={() => clearSlot(activeSlot)} className={`${btnSecondary} text-red-600 dark:text-red-400 border-red-600/40 dark:border-red-400/40`}>
                  <Trash2 size={14} /> Retirer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* TAB: REVIEWS */}
        {tab === "reviews" && (
          <div>
            <div className="flex flex-wrap gap-2 mb-5">
              {[
                { key: "pending", label: "En attente", icon: Clock },
                { key: "approved", label: "Approuvés", icon: CheckCircle2 },
                { key: "rejected", label: "Refusés", icon: XCircle },
              ].map(({ key, label, icon: Ico }) => (
                <button
                  key={key}
                  data-testid={`admin-filter-${key}`}
                  onClick={() => setReviewFilter(key)}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs border transition-colors ${reviewFilter === key ? "bg-forest text-ivory border-forest dark:bg-gold dark:text-night dark:border-gold" : "border-beige dark:border-charcoal text-stone dark:text-mist hover:border-forest dark:hover:border-gold"}`}
                >
                  <Ico size={13} /> {label} <span className="opacity-70">({counts[key] || 0})</span>
                </button>
              ))}
            </div>

            {reviewsLoading && <p className="text-sm text-stone dark:text-mist">Chargement…</p>}
            {!reviewsLoading && reviews.length === 0 && (
              <p data-testid="admin-reviews-empty" className="text-sm text-stone dark:text-mist italic py-6 text-center">
                Aucun avis dans cette catégorie.
              </p>
            )}

            <div className="space-y-3">
              {reviews.map((r) => (
                <div key={r.id} data-testid={`admin-review-${r.id}`} className="p-4 rounded-xl border border-beige dark:border-charcoal bg-sand/40 dark:bg-onyx/40">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <p className="font-medium text-ink dark:text-whisper">{r.name}{r.location ? <span className="text-stone dark:text-mist font-normal"> · {r.location}</span> : ""}</p>
                      <div className="flex items-center gap-1 mt-1">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Star key={i} size={12} className={i <= r.rating ? "text-gold fill-gold" : "text-stone/30 dark:text-mist/30"} strokeWidth={1.5} />
                        ))}
                        <span className="text-[10px] text-stone dark:text-mist ml-2">{new Date(r.created_at).toLocaleDateString("fr-FR")}</span>
                      </div>
                    </div>
                    <span className={`text-[10px] tracking-[0.15em] uppercase px-2 py-1 rounded-full ${
                      r.status === "approved" ? "bg-forest/10 text-forest dark:bg-gold/15 dark:text-gold"
                      : r.status === "rejected" ? "bg-red-500/10 text-red-600 dark:text-red-400"
                      : "bg-beige dark:bg-charcoal text-stone dark:text-mist"
                    }`}>{r.status}</span>
                  </div>
                  <p className="text-sm text-stone dark:text-mist leading-relaxed mb-3 whitespace-pre-wrap">{r.message}</p>
                  <div className="flex flex-wrap gap-2">
                    {r.status !== "approved" && (
                      <button data-testid={`admin-approve-${r.id}`} onClick={() => moderateReview(r.id, "approved")} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-forest text-ivory hover:bg-forest-hover dark:bg-gold dark:text-night">
                        <CheckCircle2 size={12} /> Approuver
                      </button>
                    )}
                    {r.status !== "rejected" && (
                      <button data-testid={`admin-reject-${r.id}`} onClick={() => moderateReview(r.id, "rejected")} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-stone/40 dark:border-mist/40 text-stone dark:text-mist hover:border-red-600 hover:text-red-600">
                        <XCircle size={12} /> Refuser
                      </button>
                    )}
                    {r.status !== "pending" && (
                      <button data-testid={`admin-repend-${r.id}`} onClick={() => moderateReview(r.id, "pending")} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-stone/40 dark:border-mist/40 text-stone dark:text-mist hover:border-forest dark:hover:border-gold">
                        <Clock size={12} /> En attente
                      </button>
                    )}
                    <button data-testid={`admin-delete-${r.id}`} onClick={() => deleteReview(r.id)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full text-red-600 dark:text-red-400 hover:bg-red-500/10">
                      <Trash2 size={12} /> Supprimer
                    </button>
                    <select
                      data-testid={`admin-review-category-${r.id}`}
                      value={r.category || "soin"}
                      onChange={(e) => moderateReview(r.id, r.status, e.target.value)}
                      aria-label="Catégorie de l'avis (page d'affichage)"
                      title="Page d'affichage : Soin = accueil · Formation = page Formation"
                      className="text-xs bg-ivory dark:bg-night border border-beige dark:border-charcoal rounded-full px-2.5 py-1.5 text-stone dark:text-mist ml-auto focus:outline-none focus:border-forest dark:focus:border-gold"
                    >
                      <option value="soin">Affiché : Accueil</option>
                      <option value="formation">Affiché : Formation</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB: FORMATION DATES */}
        {tab === "dates" && (
          <div>
            <p className="text-xs text-stone dark:text-mist mb-5 leading-relaxed">
              Gérez les <strong>dates de formation</strong> affichées sur la page Formation, rubrique « Prochaines sessions ».
            </p>

            {/* Create / edit form */}
            <div className="rounded-xl border border-beige dark:border-charcoal bg-sand/40 dark:bg-onyx/40 p-5 mb-6 space-y-4">
              <p className="text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist">
                {editingDateId ? "Modifier la session" : "Nouvelle session"}
              </p>
              <div>
                <label className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-1.5">Intitulé *</label>
                <input data-testid="admin-df-label" value={dateForm.label} onChange={changeDate("label")} placeholder="Ex. Session d'automne — Énergéticien de la Conscience" className={inputCls} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-1.5">Date de début *</label>
                  <input data-testid="admin-df-start" type="date" value={dateForm.start_date} onChange={changeDate("start_date")} className={inputCls} />
                </div>
                <div>
                  <label className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-1.5">Date de fin (optionnel)</label>
                  <input data-testid="admin-df-end" type="date" value={dateForm.end_date} onChange={changeDate("end_date")} className={inputCls} />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-1.5">Lieu (optionnel)</label>
                  <input data-testid="admin-df-location" value={dateForm.location} onChange={changeDate("location")} placeholder="Ex. Paris, ou En ligne" className={inputCls} />
                </div>
                <div>
                  <label className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-1.5">Statut</label>
                  <select data-testid="admin-df-status" value={dateForm.status} onChange={changeDate("status")} className={inputCls}>
                    <option value="ouverte">Ouverte aux inscriptions</option>
                    <option value="complete">Complète</option>
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-3 text-sm text-stone dark:text-mist">
                <input data-testid="admin-df-active" type="checkbox" checked={dateForm.active} onChange={changeDate("active")} className="h-4 w-4 accent-forest" />
                Session visible sur la page Formation
              </label>
              <div className="flex flex-wrap gap-3 pt-1">
                <button data-testid="admin-df-save" onClick={saveDate} className={btnPrimary}>
                  <Save size={14} /> {editingDateId ? "Mettre à jour" : "Ajouter la session"}
                </button>
                {editingDateId && (
                  <button data-testid="admin-df-cancel" onClick={() => { setEditingDateId(null); setDateForm({ ...emptyDate }); }} className={btnSecondary}>
                    Annuler la modification
                  </button>
                )}
              </div>
            </div>

            {/* Existing sessions list */}
            {datesLoading && <p className="text-sm text-stone dark:text-mist">Chargement…</p>}
            {!datesLoading && dates.length === 0 && (
              <p data-testid="admin-dates-empty" className="text-sm text-stone dark:text-mist italic py-4 text-center">
                Aucune session configurée pour le moment.
              </p>
            )}
            <div className="space-y-3">
              {dates.map((d) => (
                <div key={d.id} data-testid={`admin-date-item-${d.id}`} className="p-4 rounded-xl border border-beige dark:border-charcoal bg-sand/40 dark:bg-onyx/40">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-ink dark:text-whisper">{d.label}</p>
                      <p className="text-xs text-stone dark:text-mist mt-1">
                        {formatSessionRange(d.start_date, d.end_date)}{d.location ? ` · ${d.location}` : ""}
                      </p>
                    </div>
                    <span className={`text-[10px] tracking-[0.15em] uppercase px-2 py-1 rounded-full shrink-0 ${
                      !d.active ? "bg-beige dark:bg-charcoal text-stone dark:text-mist"
                      : d.status === "complete" ? "bg-red-500/10 text-red-600 dark:text-red-400"
                      : "bg-forest/10 text-forest dark:bg-gold/15 dark:text-gold"
                    }`}>
                      {!d.active ? "Masquée" : d.status === "complete" ? "Complète" : "Ouverte"}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button data-testid={`admin-df-edit-${d.id}`} onClick={() => editDate(d)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-stone/40 dark:border-mist/40 text-stone dark:text-mist hover:border-forest dark:hover:border-gold">
                      Modifier
                    </button>
                    <button data-testid={`admin-df-delete-${d.id}`} onClick={() => deleteDate(d.id)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full text-red-600 dark:text-red-400 hover:bg-red-500/10">
                      <Trash2 size={12} /> Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB: YOUTUBE VIDEO */}
        {tab === "video" && (
          <div>
            <p className="text-xs text-stone dark:text-mist mb-5 leading-relaxed">
              Choisissez la <strong>vidéo ou la playlist YouTube</strong> mise en avant sur l'accueil et la page Formation. Collez le code d'intégration (iframe), un lien de vidéo ou de playlist — le format est détecté automatiquement.
            </p>

            <div className="rounded-xl border border-beige dark:border-charcoal bg-sand/40 dark:bg-onyx/40 p-5 mb-6 space-y-4">
              <div>
                <label className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-1.5">
                  Code iframe YouTube ou lien
                </label>
                <textarea
                  data-testid="admin-yt-input"
                  rows={3}
                  value={ytInput}
                  onChange={(e) => setYtInput(e.target.value)}
                  placeholder='<iframe src="https://www.youtube.com/embed/..." …></iframe> — ou — https://www.youtube.com/watch?v=…&list=…'
                  className={`${inputCls} resize-none font-mono text-xs`}
                />
              </div>
              <div className="flex flex-wrap gap-3">
                <button data-testid="admin-yt-save" onClick={saveYoutube} className={btnPrimary}>
                  <Save size={14} /> Mettre à jour la vidéo
                </button>
              </div>
            </div>

            <p className="text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-3">Aperçu actuel</p>
            {ytLoading && <p className="text-sm text-stone dark:text-mist">Chargement…</p>}
            {!ytLoading && ytCurrent && (
              <div data-testid="admin-yt-preview" className="relative overflow-hidden rounded-xl border border-beige dark:border-charcoal bg-black aspect-video max-w-lg">
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={ytCurrent}
                  title="Aperçu de la vidéo YouTube mise en avant"
                  frameBorder="0"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}
          </div>
        )}

        {/* Status */}
        {status.state !== "idle" && (
          <p data-testid="admin-status" className={`text-sm mt-5 ${status.state === "ok" ? "text-forest dark:text-gold" : status.state === "err" ? "text-red-600 dark:text-red-400" : "text-stone dark:text-mist"}`}>
            {status.msg}
          </p>
        )}

        <p className="text-xs text-stone dark:text-mist pt-6 leading-relaxed">
          Token configuré côté serveur dans <code className="px-1.5 py-0.5 bg-sand/85 dark:bg-onyx/85 rounded">/app/backend/.env</code> (variable <code>ADMIN_TOKEN</code>).
        </p>
      </div>
    </div>
  );
}

// ============== SECTION TITLE ==============
function SectionTitle({ eyebrow, title, lead, center = false }) {
  return (
    <div className={`max-w-3xl ${center ? "mx-auto text-center" : ""} mb-14`}>
      <div className={`flex items-center gap-3 mb-5 ${center ? "justify-center" : ""}`}>
        <span className="h-px w-10 bg-gold" />
        <span className="text-[11px] tracking-[0.3em] uppercase text-stone dark:text-mist">{eyebrow}</span>
        <span className="h-px w-10 bg-gold" />
      </div>
      <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-ink dark:text-whisper leading-tight">
        {title}
      </h2>
      {lead && (
        <p className="mt-5 text-stone dark:text-mist text-base sm:text-lg leading-relaxed">{lead}</p>
      )}
    </div>
  );
}

// ============== RDV ==============
// ============== LA MÉTHODE (présentation de l'approche, accueil) ==============
function Methode() {
  return (
    <section id="methode" data-testid="section-methode" className="py-24 sm:py-32 bg-sand/85 dark:bg-onyx/85">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
        <SectionTitle eyebrow="La méthode" title="Qu'est-ce que l'Énergétique de la Conscience ?" center />
        <Reveal>
          <div className="space-y-5 text-stone dark:text-mist leading-relaxed text-base sm:text-lg">
            <p>
              L'Énergétique de la Conscience est l'approche développée par Denis Tran pour explorer
              les relations entre le corps, les perceptions, l'énergie et la conscience.
            </p>
            <p>
              Elle s'inspire notamment du Qi Gong, de la Médecine Traditionnelle Chinoise, du Taoïsme,
              de la méditation et de l'expérience du massage.
            </p>
            <p>
              Elle propose des pratiques d'attention, de respiration, de visualisation, de perception
              corporelle et de travail énergétique dans une démarche d'exploration et d'autonomie.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <p data-testid="methode-triptyque" className="mt-12 font-serif text-2xl sm:text-3xl tracking-[0.14em] uppercase text-forest dark:text-gold leading-snug">
            Corps — Énergie — Conscience
          </p>
        </Reveal>
        <Reveal delay={0.18}>
          <div className="mt-12 flex flex-wrap justify-center gap-3">
            <Link data-testid="methode-cta-decouvrir" to="/energetique-de-la-conscience" className={btnPrimary}>
              Découvrir l'Énergétique de la Conscience <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ============== QUICK ACCESS ==============
const QUICK = [
  { icon: Calendar, label: "Rdv.", desc: "Réserver une séance", href: "https://tidycal.com/denistran", external: true, testid: "quick-rdv" },
  { icon: Heart, label: "Soins spirituels", desc: "Une approche énergétique par le Qi et la conscience.", href: "/#declaration", testid: "quick-soins" },
  { icon: GraduationCap, label: "Webinaires", desc: "Cours en ligne & replays", href: "https://denistran.onlinecoursehost.com/", external: true, testid: "quick-webinaires" },
  { icon: BookOpen, label: "Ebooks", desc: "Lectures & enseignements", href: "https://zylvie.com/le-guerisseur", external: true, testid: "quick-ebooks" },
  { icon: MessageCircle, label: "Chat — Discuter en ligne", desc: "Assistant IA — Posez vos questions", action: "chat", testid: "quick-chat" },
  { icon: CalendarDays, label: "Calendrier", desc: "Agenda complet", href: "https://cicd4v3nxfqr6.mocha.app/", external: true, testid: "quick-calendrier" },
];

function QuickAccess({ onChatOpen }) {
  return (
    <section data-testid="section-quick" className="py-24 sm:py-32 bg-ivory/85 dark:bg-night/85">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionTitle
          eyebrow="Accès rapides"
          title="L'essentiel en un regard"
          center
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {QUICK.map((q) => {
            const isChat = q.action === "chat";
            const common = {
              "data-testid": q.testid,
              className: "group flex items-start gap-5 p-7 rounded-2xl border border-beige dark:border-charcoal bg-sand/60 dark:bg-onyx/60 hover:bg-sand dark:hover:bg-onyx hover:-translate-y-0.5 transition-all duration-300 text-left w-full",
            };
            const inner = (
              <>
                <div className="shrink-0 h-12 w-12 rounded-full bg-ivory/85 dark:bg-night/85 border border-beige dark:border-charcoal flex items-center justify-center group-hover:border-gold transition-colors">
                  <q.icon className="text-forest dark:text-gold" size={20} strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-ink dark:text-whisper mb-1">{q.label}</h3>
                  <p className="text-sm text-stone dark:text-mist">{q.desc}</p>
                </div>
              </>
            );
            if (isChat) {
              return (
                <button key={q.testid} {...common} onClick={onChatOpen} type="button">{inner}</button>
              );
            }
            return (
              <a
                key={q.testid}
                {...common}
                href={q.href}
                target={q.external ? "_blank" : undefined}
                rel={q.external ? "noopener noreferrer" : undefined}
              >
                {inner}
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============== YOUTUBE ==============
function YouTubeSection() {
  return (
    <section id="youtube" data-testid="section-youtube" className="py-24 sm:py-32 relative overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1440407876336-62333a6f010f?auto=format&fit=crop&q=80&w=2000"
          alt="Forêt apaisante au lever du jour"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-ivory/85 dark:bg-night/90" />
      </div>
      <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
        <div className="flex items-center justify-center gap-3 mb-5">
          <span className="h-px w-10 bg-gold" />
          <span className="text-[11px] tracking-[0.3em] uppercase text-stone dark:text-mist">Vidéos</span>
          <span className="h-px w-10 bg-gold" />
        </div>
        <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-ink dark:text-whisper">
          Ma chaîne YouTube officielle
        </h2>
        <p className="mt-5 text-stone dark:text-mist text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
          Avec plus de <span className="text-forest dark:text-gold font-medium">1000 vidéos</span> :
          enseignements, soins, partages et témoignages. Abonnez-vous pour recevoir chaque nouveauté.
        </p>
        <a
          data-testid="cta-youtube"
          href="https://www.youtube.com/@le-guerisseur"
          target="_blank" rel="noopener noreferrer"
          className={`${btnPrimary} mt-10`}
        >
          <Youtube size={16} /> S'abonner à la chaîne
        </a>
      </div>
    </section>
  );
}

// ============== CONTENUS & CRÉATIONS ==============
const CONTENT = [
  {
    icon: GraduationCap,
    title: "Tous mes cours en ligne passionnants",
    desc: "Webinaires en replay et e-books — explorations approfondies, à votre rythme.",
    testid: "content-cours",
    href: "https://denistran.onlinecoursehost.com/",
    external: true,
  },
  {
    icon: Music,
    title: "Toutes mes créations musicales originales",
    desc: "Soul Sounds Good est l'Âme-Music — vibrations sonores composées pour accompagner l'écoute du corps.",
    testid: "content-music",
    href: "https://hearthis.at/soul-sounds-good/",
    external: true,
  },
  {
    icon: FileText,
    title: "Tous mes articles fascinants",
    desc: "Découvrez les résumés de mes formations et mes lives — lecture libre et inspirante.",
    testid: "content-articles",
    href: "https://le-guerisseur.videotoblog.ai/",
    external: true,
  },
  {
    icon: BookOpen,
    title: "Tous mes E-Book incroyables",
    desc: "Intention, abondance, astrologie et bien plus — des écrits pour cheminer.",
    testid: "content-ebooks",
    href: "https://zylvie.com/le-guerisseur",
    external: true,
  },
];

function Contenus() {
  return (
    <section id="contenus" data-testid="section-contenus" className="py-24 sm:py-32 bg-sand/85 dark:bg-onyx/85">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionTitle
          eyebrow="Contenus & créations"
          title="Mes œuvres, à votre écoute"
          lead="Cours, musique, articles et ebooks — un corpus vivant nourri d'expérience et de partage."
        />
        <div className="grid md:grid-cols-2 gap-5 lg:gap-6">
          {CONTENT.map((c) => (
            <a
              key={c.testid}
              data-testid={c.testid}
              href={c.href}
              target={c.external ? "_blank" : undefined}
              rel={c.external ? "noopener noreferrer" : undefined}
              className="group flex flex-col gap-5 p-8 lg:p-10 rounded-2xl border border-beige dark:border-charcoal bg-ivory/85 dark:bg-night/85 hover:-translate-y-1 transition-all duration-500"
            >
              <c.icon className="text-forest dark:text-gold" size={26} strokeWidth={1.5} />
              <h3 className="font-serif text-xl sm:text-2xl text-ink dark:text-whisper leading-snug">{c.title}</h3>
              <p className="text-stone dark:text-mist leading-relaxed">{c.desc}</p>
              <span className="inline-flex items-center gap-2 text-[12px] tracking-[0.16em] uppercase text-forest dark:text-gold mt-2">
                Découvrir <ArrowRight size={14} />
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============== ORACLE ==============
function Oracle() {
  return (
    <section id="oracle" data-testid="section-oracle" className="py-24 sm:py-32 bg-ivory/85 dark:bg-night/85">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="relative">
            <img
              src="https://customer-assets.emergentagent.com/job_qi-gateway/artifacts/ni7eo84e_Mockup%20Oracle%20Amourai%CC%82.png"
              alt="Oracle de l'Amouraî — coffret et jeu de 25 cartes spirituelles"
              className="w-full h-[480px] object-cover rounded-2xl border border-beige dark:border-charcoal bg-black"
            />
            <div className="absolute -bottom-5 -right-5 bg-ivory/85 dark:bg-night/85 border border-beige dark:border-charcoal rounded-full px-6 py-3 shadow-md">
              <span className="font-serif text-xl text-forest dark:text-gold">19,9 €</span>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-gold" />
              <span className="text-[11px] tracking-[0.3em] uppercase text-stone dark:text-mist">Oracle</span>
            </div>
            <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-ink dark:text-whisper leading-tight">
              Oracle de l'Amourai
            </h2>
            <p className="mt-6 text-stone dark:text-mist leading-relaxed text-base sm:text-lg">
              Un jeu de <strong className="text-ink dark:text-whisper font-medium">25 cartes</strong> conçu comme un pont
              vivant entre le corps physique, l'énergie vitale et l'essence spirituelle. Un outil d'écoute de soi,
              du corps et du cœur — à offrir, à pratiquer, à méditer.
            </p>

            <ul className="mt-8 space-y-3 text-stone dark:text-mist">
              <li className="flex items-start gap-3"><Star className="text-gold mt-1 shrink-0" size={16} strokeWidth={1.5}/>Jeu de 25 cartes finement illustrées</li>
              <li className="flex items-start gap-3"><Star className="text-gold mt-1 shrink-0" size={16} strokeWidth={1.5}/>Pont entre corps, énergie et esprit</li>
              <li className="flex items-start gap-3"><Star className="text-gold mt-1 shrink-0" size={16} strokeWidth={1.5}/>Disponible prochainement sur Android, iOS et en version cartes physiques</li>
            </ul>

            <a
              data-testid="cta-oracle"
              href="https://amourai.com"
              target="_blank" rel="noopener noreferrer"
              className={`${btnPrimary} mt-10`}
            >
              Découvrir l'Oracle <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============== REVIEWS (public) ==============
// Reusable: tid prefixes all data-testids ("" = landing defaults, "formation-" = /formation copy)
// category: null = all reviews, "formation" = student testimonials only
function Reviews({
  sectionId = "avis",
  tid = "",
  eyebrow = "Avis vérifiés",
  title = "Témoignages & ressentis",
  lead = "Chaque avis est lu et vérifié avant d'être publié. Vous pouvez à votre tour partager votre expérience.",
  withForm = true,
  bgClass = "bg-ivory/85 dark:bg-night/85",
  category = null,
  emptyText = "Soyez le premier à laisser votre témoignage.",
  formTitle = "Laisser un avis",
  formLead = "Votre témoignage sera visible après vérification.",
  grid = false,
  allHref = null,
}) {
  const [reviews, setReviews] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [stats, setStats] = useState({ average: 0, count: 0 });
  const [form, setForm] = useState({ name: "", rating: 5, message: "", location: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews${category ? `?category=${category}` : ""}`);
      setReviews(data.reviews || []);
      setStats(data.stats || { average: 0, count: 0 });
    } catch (e) { /* silent */ }
    finally { setLoaded(true); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || form.message.trim().length < 10) {
      setStatus({ state: "err", msg: "Merci d'indiquer votre prénom et un message d'au moins 10 caractères." });
      return;
    }
    setStatus({ state: "sending", msg: "Envoi…" });
    try {
      const { data } = await axios.post(`${API}/reviews`, { ...form, ...(category ? { category } : {}) });
      // Send a notification email to the admin (best-effort, non-blocking UX)
      const stars = "★".repeat(form.rating) + "☆".repeat(5 - form.rating);
      const locLine = form.location ? `Ville : ${form.location}\n` : "";
      const catLine = category === "formation" ? `Page   : Formation (élève / stagiaire)\n` : "";
      sendWeb3Form({
        subject: `[Le guérisseur] Nouvel avis${category === "formation" ? " — Formation" : ""} de ${form.name} (${form.rating}/5)`,
        fromName: "Le guérisseur — Avis",
        message:
          `Nouvel avis à modérer sur le-guerisseur.com\n\n` +
          `Auteur : ${form.name}\n` +
          `${locLine}` +
          `${catLine}` +
          `Note   : ${stars} (${form.rating}/5)\n\n` +
          `Message :\n${form.message}\n\n` +
          `---\n` +
          `Connectez-vous au dashboard admin (icône bouclier dans la nav) → onglet « Avis » pour approuver ou refuser.`,
      });
      setStatus({ state: "ok", msg: data.message || "Merci pour votre avis." });
      setForm({ name: "", rating: 5, message: "", location: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Une erreur est survenue.";
      setStatus({ state: "err", msg });
    }
  };

  const renderReviewCard = (r) => (
    <article className="relative h-full p-7 rounded-2xl border border-beige dark:border-charcoal bg-sand/60 dark:bg-onyx/60 flex flex-col">
      <Quote className="absolute top-5 right-5 text-gold/40" size={20} />
      <div className="flex items-center gap-1 mb-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star key={i} size={14} className={i <= r.rating ? "text-gold fill-gold" : "text-stone/30 dark:text-mist/30"} strokeWidth={1.5} />
        ))}
      </div>
      <p className="text-stone dark:text-mist leading-relaxed text-sm flex-1 whitespace-pre-wrap">{r.message}</p>
      <div className="mt-5 pt-4 border-t border-beige dark:border-charcoal">
        <p className="font-serif text-lg text-ink dark:text-whisper">{r.name}</p>
        {r.location && <p className="text-xs text-stone dark:text-mist mt-0.5">{r.location}</p>}
      </div>
    </article>
  );

  // Display-only variant (e.g. /formation): hide entirely until there is something to show
  if (!withForm && (!loaded || reviews.length === 0)) return null;

  return (
    <section id={sectionId || undefined} data-testid={`${tid}section-reviews`} className={`py-24 sm:py-32 ${bgClass}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionTitle
          eyebrow={eyebrow}
          title={title}
          lead={lead}
          center
        />

        {/* Stats */}
        {stats.count > 0 && (
          <div data-testid={`${tid}reviews-stats`} className="flex flex-col items-center justify-center mb-12">
            <div className="flex items-center gap-1.5">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} size={22} className={i <= Math.round(stats.average) ? "text-gold fill-gold" : "text-stone/30 dark:text-mist/30"} strokeWidth={1.5} />
              ))}
            </div>
            <p className="mt-3 text-stone dark:text-mist text-sm tracking-wide">
              <span className="font-serif text-2xl text-ink dark:text-whisper">{stats.average.toFixed(1).replace(".", ",")}</span>
              <span className="mx-2">·</span>
              {stats.count} avis vérifié{stats.count > 1 ? "s" : ""}
            </p>
          </div>
        )}

        {/* Reviews carousel / full grid */}
        {reviews.length === 0 ? (
          <div data-testid={`${tid}reviews-empty`} className={`${withForm ? "mb-16" : ""} text-center py-12 border border-dashed border-beige dark:border-charcoal rounded-2xl max-w-2xl mx-auto`}>
            <Quote size={28} className="text-gold mx-auto mb-3" strokeWidth={1.2} />
            <p className="text-stone dark:text-mist italic">{emptyText}</p>
          </div>
        ) : grid ? (
          <div className={withForm ? "mb-16" : ""}>
            <div data-testid={`${tid}reviews-grid`} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {reviews.map((r) => (
                <div key={r.id} data-testid={`${tid}review-${r.id}`}>{renderReviewCard(r)}</div>
              ))}
            </div>
          </div>
        ) : (
          <div className={allHref ? "mb-8" : withForm ? "mb-16" : ""}>
            <Carousel
              data-testid={`${tid}reviews-carousel`}
              opts={{ align: "start", loop: reviews.length > 3 }}
              plugins={[Autoplay({ delay: 6000, stopOnInteraction: true, stopOnMouseEnter: true })]}
              className="w-full"
            >
              <CarouselContent className="-ml-4">
                {reviews.map((r) => (
                  <CarouselItem
                    key={r.id}
                    data-testid={`${tid}review-${r.id}`}
                    className="pl-4 basis-full md:basis-1/2 lg:basis-1/3"
                  >
                    {renderReviewCard(r)}
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious data-testid={`${tid}reviews-prev`} className="hidden sm:flex -left-3 lg:-left-6 border-beige dark:border-charcoal bg-ivory/90 dark:bg-night/90 hover:bg-sand dark:hover:bg-onyx text-forest dark:text-gold" />
              <CarouselNext data-testid={`${tid}reviews-next`} className="hidden sm:flex -right-3 lg:-right-6 border-beige dark:border-charcoal bg-ivory/90 dark:bg-night/90 hover:bg-sand dark:hover:bg-onyx text-forest dark:text-gold" />
            </Carousel>
          </div>
        )}

        {/* Lien vers la page Témoignages dédiée (accueil) */}
        {allHref && !grid && reviews.length > 0 && (
          <div className={`text-center ${withForm ? "mb-16" : ""}`}>
            <Link data-testid={`${tid}reviews-all-link`} to={allHref} className={btnSecondary}>
              Voir tous les témoignages <ArrowRight size={14} />
            </Link>
          </div>
        )}

        {/* Submission form (landing only) */}
        {withForm && (
        <div className="max-w-2xl mx-auto bg-sand/85 dark:bg-onyx/85 rounded-2xl border border-beige dark:border-charcoal p-8 sm:p-10">
          <h3 className="font-serif text-2xl sm:text-3xl text-ink dark:text-whisper mb-2">{formTitle}</h3>
          <p className="text-sm text-stone dark:text-mist mb-6">{formLead}</p>
          <form onSubmit={submit} data-testid={`${tid}review-form`} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-1.5">Prénom *</label>
                <input data-testid={`${tid}review-name`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-ivory/85 dark:bg-night/85 border border-beige dark:border-charcoal rounded-lg px-4 py-2.5 text-ink dark:text-whisper focus:outline-none focus:border-forest dark:focus:border-gold" />
              </div>
              <div>
                <label className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-1.5">Ville (optionnel)</label>
                <input data-testid={`${tid}review-location`} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full bg-ivory/85 dark:bg-night/85 border border-beige dark:border-charcoal rounded-lg px-4 py-2.5 text-ink dark:text-whisper focus:outline-none focus:border-forest dark:focus:border-gold" />
              </div>
            </div>
            <div>
              <label className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-1.5">Note</label>
              <div data-testid={`${tid}review-rating`} className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button
                    type="button"
                    key={i}
                    data-testid={`${tid}review-star-${i}`}
                    onClick={() => setForm({ ...form, rating: i })}
                    aria-label={`Mettre ${i} étoile${i > 1 ? "s" : ""}`}
                    className="p-1"
                  >
                    <Star size={22} className={i <= form.rating ? "text-gold fill-gold" : "text-stone/40 dark:text-mist/40"} strokeWidth={1.5} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-1.5">Votre témoignage *</label>
              <textarea data-testid={`${tid}review-message`} rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-ivory/85 dark:bg-night/85 border border-beige dark:border-charcoal rounded-lg px-4 py-2.5 text-ink dark:text-whisper focus:outline-none focus:border-forest dark:focus:border-gold resize-none" />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
              <button data-testid={`${tid}review-submit`} type="submit" disabled={status.state === "sending"} className={`${btnPrimary} disabled:opacity-50`}>
                <Send size={14} /> {status.state === "sending" ? "Envoi…" : "Publier mon avis"}
              </button>
              {status.state !== "idle" && (
                <p data-testid={`${tid}review-status`} className={`text-sm ${status.state === "ok" ? "text-forest dark:text-gold" : status.state === "err" ? "text-red-600 dark:text-red-400" : "text-stone dark:text-mist"}`}>
                  {status.msg}
                </p>
              )}
            </div>
          </form>
        </div>
        )}
      </div>
    </section>
  );
}

// ============== RESSOURCES THÉRAPEUTES ==============
const RESOURCES = [
  { name: "Clic Thérapeute", desc: "Visibilité en ligne et annuaire pour praticiens.", href: "https://clictherapeute.fr", testid: "res-clictherapeute" },
];

function Ressources() {
  return (
    <section id="ressources" data-testid="section-ressources" className="py-24 sm:py-32 bg-sand/85 dark:bg-onyx/85 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionTitle
          eyebrow="Espace praticiens"
          title="Ressources pour thérapeutes"
          lead="Un espace de ressources, de visibilité et de mise en relation pour celles et ceux qui accompagnent."
        />
        <div className="grid md:grid-cols-3 gap-5 lg:gap-6 max-w-md md:max-w-none mx-auto">
          {RESOURCES.length === 1 && <div className="hidden md:block" aria-hidden="true" />}
          {RESOURCES.map((r) => (
            <a
              key={r.testid}
              data-testid={r.testid}
              href={r.href}
              target="_blank" rel="noopener noreferrer"
              className="group flex flex-col p-8 rounded-2xl border border-beige dark:border-charcoal bg-ivory/85 dark:bg-night/85 hover:-translate-y-1 transition-all duration-500"
            >
              <Users className="text-forest dark:text-gold mb-6" size={24} strokeWidth={1.5} />
              <h3 className="font-serif text-xl sm:text-2xl text-ink dark:text-whisper mb-3">{r.name}</h3>
              <p className="text-stone dark:text-mist leading-relaxed text-sm">{r.desc}</p>
              <span className="inline-flex items-center gap-2 text-[12px] tracking-[0.16em] uppercase text-forest dark:text-gold mt-6">
                Visiter <ExternalLink size={14} />
              </span>
            </a>
          ))}
          {RESOURCES.length === 1 && <div className="hidden md:block" aria-hidden="true" />}
        </div>
      </div>
    </section>
  );
}

// TikTok glyph (lucide doesn't ship one)
const TikTokIcon = ({ size = 20 }) => (
  <span className="font-bold" style={{ fontSize: size }}>T</span>
);

// ============== SOCIALS ==============
function Socials() {
  // <!-- ============== SOCIALS: remplacer l'URL Facebook ci-dessous si besoin ============== -->
  const FB_URL = "https://www.facebook.com/therapeutesansfrontieres";
  // <!-- ============== INSTAGRAM ============== -->
  const IG_URL = "https://www.instagram.com/amourai/";

  const socials = [
    { Icon: Instagram, name: "Instagram", href: IG_URL, testid: "social-instagram" },
    { Icon: TikTokIcon, name: "TikTok", href: "https://www.tiktok.com/@le_guerisseur", testid: "social-tiktok" },
    { Icon: Youtube, name: "YouTube", href: "https://www.youtube.com/@le-guerisseur", testid: "social-youtube" },
    { Icon: Facebook, name: "Facebook", href: FB_URL, testid: "social-facebook" },
  ];

  return (
    <section data-testid="section-socials" className="py-24 sm:py-32 bg-ivory/85 dark:bg-night/85">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
        <SectionTitle
          eyebrow="Restons en lien"
          title="Vous me découvrez sur les réseaux sociaux ?"
          lead="Retrouvez mes lives, mes démonstrations, mes réflexions et mes enseignements sur les réseaux sociaux."
          center
        />
        <div className="mb-12">
          <a
            data-testid="socials-cta-lives"
            href="https://www.tiktok.com/@le_guerisseur"
            target="_blank" rel="noopener noreferrer"
            className={btnPrimary}
          >
            Voir les prochains lives <ArrowRight size={14} />
          </a>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {socials.map(({ Icon, name, href, testid }) => (
            <a
              key={testid}
              data-testid={testid}
              href={href}
              target="_blank" rel="noopener noreferrer"
              className="group flex flex-col items-center gap-3 p-8 rounded-2xl border border-beige dark:border-charcoal bg-sand/50 dark:bg-onyx/50 hover:bg-sand dark:hover:bg-onyx hover:-translate-y-1 transition-all duration-300"
            >
              <div className="h-12 w-12 rounded-full bg-ivory/85 dark:bg-night/85 border border-beige dark:border-charcoal flex items-center justify-center text-forest dark:text-gold group-hover:border-gold transition-colors">
                <Icon size={20} strokeWidth={1.5} />
              </div>
              <span className="text-sm tracking-[0.12em] uppercase text-stone dark:text-mist">{name}</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============== DÉCLARATION SPIRITUELLE ÉCLAIRÉE ==============
function Declaration() {
  return (
    <section id="declaration" data-testid="section-declaration" className="py-24 sm:py-32 bg-sand/85 dark:bg-onyx/85">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-10 bg-gold" />
            <span className="text-[11px] tracking-[0.3em] uppercase text-stone dark:text-mist">Lecture importante</span>
            <span className="h-px w-10 bg-gold" />
          </div>
          <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-ink dark:text-whisper">
            Déclaration Spirituelle Éclairée
          </h2>
          <p className="mt-5 text-stone dark:text-mist italic">
            Merci de prendre le temps de lire attentivement les informations suivantes pour mieux comprendre la nature de votre démarche personnelle.
          </p>
        </div>

        <div className="prose-spiritual space-y-6 text-stone dark:text-mist leading-relaxed text-base sm:text-lg">
          <h3 className="font-serif text-2xl text-ink dark:text-whisper mt-0">Soins spirituels par le Q.I.©</h3>

          <p>
            La rencontre que vous sollicitez avec <strong className="text-ink dark:text-whisper font-medium">Denis TRAN (Le Guérisseur)</strong> est
            une expérience holistique qui se concentre exclusivement sur votre dimension spirituelle et votre âme par le Q.I.©.
            Cette rencontre vise à favoriser une harmonisation énergétique complète de votre être, offrant ainsi une opportunité
            de communier et de stimuler la force vitale qui vous entoure et qui est présente en vous.
          </p>

          <p>En demandant une rencontre ou un travail à distance par le Q.I.© avec Le Guérisseur :</p>
          <ul className="space-y-2 list-disc list-inside">
            <li>Vous reconnaissez et acceptez qu'il agit en tant qu'intermédiaire simple entre cette source d'énergie vitale et vous.</li>
            <li>Les interventions spirituelles par le Q.I.© qui auront lieu en présentiel ou à distance seront exclusivement destinées à votre âme.</li>
          </ul>

          <p>
            Cette rencontre peut vous permettre de ressentir une connexion avec l'Énergie du Q.I.©, ou tout autre nom en accord
            avec vos croyances, la source de toute vie, dans tout votre corps, favorisant ainsi votre bien-être global.
          </p>

          <p>
            Il est important de noter que <strong className="text-ink dark:text-whisper font-medium">Denis TRAN n'est ni médecin,
            ni psychologue, ni psychiatre</strong>, et ne prétend pas l'être. Pendant cette rencontre individuelle ou pendant un
            travail à distance, il ne posera aucun diagnostic, n'établira aucun plan de traitement, ne fera aucune recommandation,
            prescription ou commentaire d'ordre médical.
          </p>

          <p>
            Les rencontres spirituelles et holistiques par le Q.I.© proposées ne doivent en aucun cas remplacer, altérer ou
            influencer votre parcours auprès de votre médecin généraliste et/ou spécialiste.
          </p>

          <p className="italic">Les rencontres spirituelles par le Q.I.© sont uniquement disponibles sur rendez-vous.</p>

          <div className="gold-divider my-10" />

          <h3 className="font-serif text-2xl text-ink dark:text-whisper">Éthique</h3>
          <ul className="space-y-2 list-disc list-inside">
            <li>Respect de la personne et de son intégrité.</li>
            <li>Liberté de croyance pleine et entière.</li>
            <li>Absence totale de manipulation.</li>
            <li>Posture d'accompagnement spirituel et non médicale.</li>
            <li>Respect du consentement et de la démarche personnelle.</li>
          </ul>

          <div className="gold-divider my-10" />

          <h3 className="font-serif text-2xl text-ink dark:text-whisper">Protection des données</h3>
          <ul className="space-y-2 list-disc list-inside">
            <li>Les données envoyées via le formulaire servent uniquement à répondre à la demande.</li>
            <li>Aucune revente de données.</li>
            <li>Collecte minimale.</li>
            <li>Conservation limitée au strict nécessaire.</li>
            <li>Possibilité de demander suppression ou modification des données à tout moment.</li>
          </ul>

          <div className="gold-divider my-10" />

          <h3 className="font-serif text-2xl text-ink dark:text-whisper">Aucun remboursement</h3>
          <p className="italic">(retard, réclamation, oubli…)</p>
        </div>
      </div>
    </section>
  );
}

// ============== FAQ SEO ==============
const FAQS = [
  {
    q: "Qu'est-ce qu'un soin spirituel par le Q.I.© ?",
    a: "C'est une expérience holistique d'harmonisation énergétique destinée à l'âme. Elle ne remplace pas un acte médical et s'inscrit dans une démarche spirituelle personnelle."
  },
  {
    q: "Peut-on agir sur la douleur à distance ?",
    a: "Le guérisseur propose un travail à distance par le Q.I.©. Il agit comme intermédiaire entre la source d'énergie vitale et la personne. Aucun diagnostic, ni traitement médical n'est posé."
  },
  {
    q: "Comment prendre rendez-vous avec Denis Tran ?",
    a: "Toutes les réservations se font via TidyCal : tidycal.com/denistran. L'agenda complet est consultable sur cicd4v3nxfqr6.mocha.app."
  },
  {
    q: "Peut-on être formé ?",
    a: "Oui — la Formation d'Énergéticien de la Conscience transmet l'approche complète en 4 jours : écoute, Qi, conscience et art de l'accompagnement. Ouverte à toutes et à tous, elle ouvre la voie aux statuts de Praticien certifié, Praticien agréé et Maître de Soi.",
    cta: { label: "Découvrir la formation", href: "/formation" },
  },
  {
    q: "Où retrouver les vidéos et les lives du guérisseur ?",
    a: "Sur la chaîne YouTube officielle (1000+ vidéos) et sur TikTok pour les directs en live."
  },
];

function Faq() {
  const [open, setOpen] = useState(null);
  return (
    <section data-testid="section-faq" className="py-24 sm:py-32 bg-ivory/85 dark:bg-night/85">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <SectionTitle eyebrow="Questions" title="Mini-FAQ" center />
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <div
              key={i}
              data-testid={`faq-item-${i}`}
              className="border border-beige dark:border-charcoal rounded-xl overflow-hidden bg-sand/40 dark:bg-onyx/40"
            >
              <button
                data-testid={`faq-trigger-${i}`}
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between p-5 sm:p-6 text-left"
              >
                <span className="font-serif text-lg sm:text-xl text-ink dark:text-whisper">{f.q}</span>
                <ChevronDown
                  size={18}
                  className={`text-forest dark:text-gold transition-transform duration-300 ${open === i ? "rotate-180" : ""}`}
                />
              </button>
              {open === i && (
                <div data-testid={`faq-answer-${i}`} className="px-5 sm:px-6 pb-6 text-stone dark:text-mist leading-relaxed">
                  {f.a}
                  {f.cta && (
                    <span className="block mt-4">
                      <Link
                        data-testid={`faq-cta-${i}`}
                        to={f.cta.href}
                        className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-medium text-forest dark:text-gold link-underline"
                      >
                        {f.cta.label} <ArrowRight size={12} />
                      </Link>
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============== CONTACT FORM ==============
function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" }); // idle | sending | ok | err

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      setStatus({ state: "err", msg: "Merci de remplir tous les champs." });
      return;
    }
    setStatus({ state: "sending", msg: "Envoi en cours…" });
    try {
      // <!-- ============== CONTACT: Web3Forms (browser-side) + persistence in backend ============== -->
      const emailContent =
        `Nouveau message depuis le-guerisseur.com\n\n` +
        `Nom    : ${form.name}\n` +
        `Email  : ${form.email}\n` +
        `Sujet  : ${form.subject}\n\n` +
        `Message :\n${form.message}\n`;

      const [emailOk, persistRes] = await Promise.allSettled([
        sendWeb3Form({
          subject: `[Le guérisseur] ${form.subject}`,
          fromName: `Le guérisseur — ${form.name}`,
          message: emailContent,
          replyTo: form.email,
        }),
        axios.post(`${API}/contact`, form),
      ]);

      const emailSent = emailOk.status === "fulfilled" && emailOk.value === true;
      const persisted = persistRes.status === "fulfilled";

      if (!emailSent && !persisted) {
        throw new Error("Échec d'envoi.");
      }
      if (!emailSent) {
        setStatus({ state: "ok", msg: "Votre message a bien été enregistré. Nous vous répondrons rapidement." });
      } else {
        const msg = persistRes.status === "fulfilled"
          ? persistRes.value?.data?.message
          : "Votre message a bien été envoyé. Merci.";
        setStatus({ state: "ok", msg: msg || "Votre message a bien été envoyé. Merci." });
      }
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Une erreur est survenue. Réessayez plus tard.";
      setStatus({ state: "err", msg });
    }
  };

  return (
    <section id="contact" data-testid="section-contact" className="py-24 sm:py-32 bg-sand/85 dark:bg-onyx/85">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <SectionTitle
          eyebrow="Contact"
          title="Écrire au guérisseur"
          lead="Pour toute question, demande de rencontre ou simple mot de gratitude — je lis chaque message."
          center
        />

        <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="name" className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-2">Nom</label>
              <input
                id="name"
                data-testid="contact-name"
                value={form.name}
                onChange={change("name")}
                type="text"
                required
                className="w-full bg-ivory/85 dark:bg-night/85 border border-beige dark:border-charcoal rounded-xl px-5 py-3.5 text-ink dark:text-whisper focus:outline-none focus:border-forest dark:focus:border-gold transition-colors"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-2">Email</label>
              <input
                id="email"
                data-testid="contact-email"
                value={form.email}
                onChange={change("email")}
                type="email"
                required
                className="w-full bg-ivory/85 dark:bg-night/85 border border-beige dark:border-charcoal rounded-xl px-5 py-3.5 text-ink dark:text-whisper focus:outline-none focus:border-forest dark:focus:border-gold transition-colors"
              />
            </div>
          </div>
          <div>
            <label htmlFor="subject" className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-2">Sujet</label>
            <input
              id="subject"
              data-testid="contact-subject"
              value={form.subject}
              onChange={change("subject")}
              type="text"
              required
              className="w-full bg-ivory/85 dark:bg-night/85 border border-beige dark:border-charcoal rounded-xl px-5 py-3.5 text-ink dark:text-whisper focus:outline-none focus:border-forest dark:focus:border-gold transition-colors"
            />
          </div>
          <div>
            <label htmlFor="message" className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-2">Message</label>
            <textarea
              id="message"
              data-testid="contact-message"
              value={form.message}
              onChange={change("message")}
              rows={6}
              required
              className="w-full bg-ivory/85 dark:bg-night/85 border border-beige dark:border-charcoal rounded-xl px-5 py-3.5 text-ink dark:text-whisper focus:outline-none focus:border-forest dark:focus:border-gold transition-colors resize-none"
            />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={status.state === "sending"}
              className={`${btnPrimary} disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <Send size={14} /> {status.state === "sending" ? "Envoi…" : "Envoyer"}
            </button>
            {status.state !== "idle" && (
              <p
                data-testid="contact-status"
                className={`text-sm ${status.state === "ok" ? "text-forest dark:text-gold" : status.state === "err" ? "text-red-600 dark:text-red-400" : "text-stone dark:text-mist"}`}
              >
                {status.msg}
              </p>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}

// ============== FOOTER ==============
function Footer({ onAdminClick }) {
  return (
    <footer data-testid="site-footer" className="bg-night text-whisper py-16 sm:py-20 border-t border-charcoal">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <h3 className="font-serif text-2xl mb-3">
              Le guérisseur <span className="text-gold">·</span>
            </h3>
            <p className="text-mist text-sm leading-relaxed">
              Apprendre à agir sur la douleur à distance — une écoute, un souffle, une conscience en action.
            </p>
            <p className="mt-4 text-mist text-sm">
              <a data-testid="footer-domain" href="https://le-guerisseur.com" className="link-underline">le-guerisseur.com</a>
            </p>
            {onAdminClick && (
              <button
                data-testid="footer-admin-btn"
                onClick={onAdminClick}
                className="mt-5 inline-flex items-center gap-2 text-xs text-mist/60 hover:text-gold transition-colors"
              >
                <Lock size={11} /> Espace administrateur
              </button>
            )}
          </div>

          <div>
            <h4 className="text-[11px] tracking-[0.3em] uppercase text-mist mb-5">Plan du site</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link data-testid="footer-link-soins" to="/soins" className="link-underline hover:text-gold">Soins individuels</Link></li>
              <li><Link data-testid="footer-link-energetique" to="/energetique-de-la-conscience" className="link-underline hover:text-gold">Énergétique de la Conscience</Link></li>
              <li><Link data-testid="footer-link-formation" to="/formation" className="link-underline hover:text-gold">Formation</Link></li>
              <li><Link data-testid="footer-link-ressources" to="/ressources" className="link-underline hover:text-gold">Ressources</Link></li>
              <li><Link data-testid="footer-link-reseaux" to="/reseaux-sociaux" className="link-underline hover:text-gold">Réseaux sociaux</Link></li>
              <li><Link data-testid="footer-link-temoignages" to="/temoignages" className="link-underline hover:text-gold">Témoignages</Link></li>
              <li><a data-testid="footer-link-declaration" href="#declaration" className="link-underline hover:text-gold">À propos</a></li>
              <li><Link data-testid="footer-link-contact" to="/contact" className="link-underline hover:text-gold">Contact</Link></li>
              <li><a data-testid="footer-link-rdv" href="https://tidycal.com/denistran" target="_blank" rel="noopener noreferrer" className="link-underline hover:text-gold">Rendez-vous</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-[11px] tracking-[0.3em] uppercase text-mist mb-5">Mes univers</h4>
            <ul className="space-y-2.5 text-sm">
              <li><a data-testid="footer-link-voie-tao" href="https://lavoielibredutao.fr" target="_blank" rel="noopener noreferrer" className="link-underline hover:text-gold">La Voie Libre du Tao</a></li>
              <li><a data-testid="footer-link-tao4c" href="https://taodes4c.fr" target="_blank" rel="noopener noreferrer" className="link-underline hover:text-gold">Le Tao des 4C</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-[11px] tracking-[0.3em] uppercase text-mist mb-5">Suivre</h4>
            <ul className="space-y-2.5 text-sm">
              {/* <!-- ============== FACEBOOK: remplacer si besoin ============== --> */}
              <li><a data-testid="footer-link-instagram" href="https://www.instagram.com/amourai/" target="_blank" rel="noopener noreferrer" className="link-underline hover:text-gold">Instagram</a></li>
              <li><a data-testid="footer-link-facebook" href="https://www.facebook.com/therapeutesansfrontieres" target="_blank" rel="noopener noreferrer" className="link-underline hover:text-gold">Facebook</a></li>
              <li><a data-testid="footer-link-tiktok" href="https://www.tiktok.com/@le_guerisseur" target="_blank" rel="noopener noreferrer" className="link-underline hover:text-gold">TikTok</a></li>
              <li><a data-testid="footer-link-youtube" href="https://www.youtube.com/@le-guerisseur" target="_blank" rel="noopener noreferrer" className="link-underline hover:text-gold">YouTube</a></li>
            </ul>
          </div>
        </div>

        <div className="gold-divider my-12" />

        <p data-testid="footer-legal" className="text-xs text-mist/80 italic leading-relaxed max-w-3xl mx-auto text-center mb-10">
          Cette approche relève du domaine du bien-être et de l'accompagnement. Elle ne constitue pas un acte médical et ne remplace ni diagnostic ni traitement réalisé par un professionnel de santé.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-mist">
          <p>© {new Date().getFullYear()} Le guérisseur — Denis Tran. Tous droits réservés.</p>
          <p className="italic">Soins spirituels par le Q.I.© — Aucune prétention médicale.</p>
        </div>
      </div>
    </footer>
  );
}

// ============== FORMATION PAGE (/formation) ==============
const FORMATION_STATUSES = [
  { value: "nouveau", label: "Nouveau candidat" },
  { value: "ancien-eleve", label: "Ancien élève" },
  { value: "praticien-certifie", label: "Praticien certifié" },
  { value: "praticien-agree", label: "Praticien agréé" },
  { value: "maitre-praticien", label: "Maître de Soi" },
];

const FORMATION_INTENTS = [
  { value: "formation-initiale", label: "Candidater à la formation initiale de 4 jours" },
  { value: "agrement", label: "Demander l'agrément" },
  { value: "maitre-praticien", label: "Candidater Maître de Soi" },
  { value: "licence-info", label: "Obtenir des informations sur la licence" },
  { value: "formateur-info", label: "Obtenir des informations sur le parcours Formateur agréé" },
];

function FormationHero() {
  return (
    <section className="relative min-h-[60vh] flex items-center pt-32 pb-16 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.10),transparent_60%),radial-gradient(ellipse_at_bottom_left,rgba(45,74,62,0.08),transparent_55%)] dark:bg-[radial-gradient(ellipse_at_top_right,rgba(229,193,88,0.12),transparent_60%),radial-gradient(ellipse_at_bottom_left,rgba(74,122,102,0.20),transparent_55%)]" />
      </div>
      <div className="relative max-w-4xl mx-auto px-6 lg:px-10 w-full text-center">
        <div className="flex items-center justify-center gap-3 mb-8">
          <span className="h-px w-12 bg-gold" />
          <span className="text-[11px] tracking-[0.3em] uppercase text-stone dark:text-mist">Formation & Licences</span>
          <span className="h-px w-12 bg-gold" />
        </div>
        <h1 data-testid="formation-h1" className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.12] text-ink dark:text-whisper">
          Apprendre à soulager la douleur à distance
        </h1>
        <p data-testid="formation-methode-4c" className="mt-6 font-serif text-xl sm:text-2xl text-forest dark:text-gold italic">
          La Méthode des 4C — Corps • Cœur • Cerveau • Conscience
        </p>
        <p data-testid="formation-approche" className="mt-5 max-w-2xl mx-auto text-sm sm:text-base text-stone dark:text-mist leading-relaxed">
          Une approche énergétique sans contact pour apprendre à accompagner la personne dans son expérience de la douleur, à proximité comme à distance.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <a data-testid="formation-cta-decouvrir" href="#formation-initiale" className={btnPrimary}>
            Découvrir la formation <ArrowRight size={14} />
          </a>
          <a data-testid="formation-cta-eleve" href="#anciens-eleves" className={btnSecondary}>
            Je suis déjà élève
          </a>
        </div>
      </div>
    </section>
  );
}

function FormationInitiale({ onCandidater }) {
  return (
    <section id="formation-initiale" data-testid="section-formation-initiale" className="py-24 sm:py-32 bg-sand/85 dark:bg-onyx/85">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <SectionTitle
          eyebrow="Le socle"
          title="La formation obligatoire — 4 jours"
          lead="La formation initiale constitue le socle obligatoire du parcours. Elle permet d'acquérir les fondamentaux de la méthode, de développer sa pratique et de préparer la validation du premier niveau."
          center
        />

        <div className="grid md:grid-cols-2 gap-6 mb-14 max-w-3xl mx-auto">
          <div className="rounded-2xl border border-gold/40 bg-ivory/70 dark:bg-night/70 p-8 text-center">
            <p className="text-[11px] tracking-[0.3em] uppercase text-stone dark:text-mist mb-3">Durée</p>
            <p className="font-serif text-5xl text-forest dark:text-gold">4 jours</p>
            <p className="mt-3 text-sm text-stone dark:text-mist">Formation en présentiel</p>
          </div>
          <div className="rounded-2xl border border-gold/40 bg-ivory/70 dark:bg-night/70 p-8 text-center">
            <p className="text-[11px] tracking-[0.3em] uppercase text-stone dark:text-mist mb-3">Investissement</p>
            <p className="font-serif text-5xl text-forest dark:text-gold">999 €</p>
            <p className="mt-3 text-sm text-stone dark:text-mist">Formation initiale complète</p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5 lg:gap-6 mb-12">
          {[
            { n: "01", t: "Apprendre", icon: BookOpen },
            { n: "02", t: "Pratiquer", icon: Compass },
            { n: "03", t: "Valider", icon: CheckCircle },
          ].map((s) => (
            <div key={s.n} className="rounded-2xl border border-beige dark:border-charcoal bg-ivory/70 dark:bg-night/70 p-7 text-center">
              <s.icon className="text-forest dark:text-gold mx-auto mb-4" size={26} strokeWidth={1.5} />
              <p className="text-[11px] tracking-[0.25em] uppercase text-gold mb-2">{s.n}</p>
              <p className="font-serif text-2xl text-ink dark:text-whisper">{s.t}</p>
            </div>
          ))}
        </div>

        <div className="max-w-3xl mx-auto rounded-xl border border-beige dark:border-charcoal bg-ivory/60 dark:bg-night/60 p-5 mb-10 text-sm text-stone dark:text-mist leading-relaxed">
          <strong className="text-ink dark:text-whisper">À noter :</strong> les 999 € correspondent <em>uniquement</em> à la formation initiale obligatoire de 4 jours. Les éventuelles licences annuelles sont distinctes et présentées séparément ci-dessous.
        </div>

        <div className="text-center">
          <button data-testid="formation-cta-candidater" onClick={onCandidater} className={btnPrimary}>
            Candidater à la formation <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </section>
  );
}

function FormationStatuts({ onSelectIntent }) {
  const statuts = [
    {
      testid: "statut-certifie",
      num: "01",
      title: "Praticien certifié",
      quote: "J'ai appris et validé la méthode.",
      desc: "Formation initiale de 4 jours, validation du parcours, certificat de formation et accès aux ressources pédagogiques.",
      price: null,
      note: null,
      cta: null,
      intent: null,
      badge: "/badges/badge-certifie.png",
    },
    {
      testid: "statut-agree",
      num: "02",
      title: "Praticien agréé",
      quote: "Je rejoins le réseau officiel.",
      desc: "Pour les anciens élèves souhaitant poursuivre leur parcours. Inclut : licence annuelle, utilisation autorisée des éléments de marque prévus par la licence, référencement dans le réseau si les critères sont remplis, ressources réservées, actualisations et perfectionnement.",
      price: "590 €/an",
      note: "Agrément soumis à candidature et validation.",
      cta: "Demander l'agrément",
      intent: "agrement",
      badge: "/badges/badge-agree.png",
    },
    {
      testid: "statut-maitre",
      num: "03",
      title: "Maître de Soi",
      quote: "J'approfondis et j'atteins un niveau avancé.",
      desc: "Pour les praticiens répondant aux critères de progression définis par l'école. Inclut : certification avancée, licence selon conditions de l'école, perfectionnement, supervision et accès aux parcours avancés.",
      price: "990 €/an",
      note: "Accès sur candidature et validation.",
      cta: "Candidater",
      intent: "maitre-praticien",
      badge: "/badges/badge-maitre.png",
    },
  ];

  return (
    <section id="statuts" data-testid="section-formation-statuts" className="py-24 sm:py-32 bg-ivory/85 dark:bg-night/85">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionTitle eyebrow="Les 3 statuts" title="Choisissez votre parcours" center />

        <div className="grid lg:grid-cols-3 gap-x-6 gap-y-16 lg:gap-8 mt-14 sm:mt-16">
          {statuts.map((s) => (
            <div
              key={s.testid}
              id={s.testid}
              data-testid={s.testid}
              className={`group relative flex flex-col rounded-2xl border p-8 lg:p-10 pt-16 lg:pt-20 transition-all duration-500 hover:-translate-y-1 ${
                s.num === "02"
                  ? "border-gold bg-sand/95 dark:bg-onyx/95 shadow-xl shadow-gold/10"
                  : "border-beige dark:border-charcoal bg-sand/70 dark:bg-onyx/70"
              }`}
            >
              <img
                src={s.badge}
                alt={`Écusson officiel — ${s.title}`}
                data-testid={`${s.testid}-badge`}
                loading="lazy"
                className="absolute -top-12 sm:-top-14 left-1/2 -translate-x-1/2 h-24 w-24 sm:h-28 sm:w-28 object-contain drop-shadow-[0_14px_24px_rgba(26,33,30,0.25)] transition-transform duration-500 group-hover:scale-105 group-hover:-rotate-2"
              />
              <p className="text-[11px] tracking-[0.25em] uppercase text-gold mb-3">{s.num}</p>
              <h3 className="font-serif text-2xl sm:text-3xl text-ink dark:text-whisper mb-4">{s.title}</h3>
              <p className="font-serif italic text-forest dark:text-gold text-lg mb-4">« {s.quote} »</p>
              <p className="text-stone dark:text-mist leading-relaxed text-sm mb-6 flex-1">{s.desc}</p>
              {s.price && (
                <div className="mb-4">
                  <p className="font-serif text-3xl text-ink dark:text-whisper">{s.price}</p>
                </div>
              )}
              {s.note && <p className="text-xs text-stone dark:text-mist italic mb-5">{s.note}</p>}
              {s.cta && (
                <button
                  data-testid={`${s.testid}-cta`}
                  onClick={() => onSelectIntent(s.intent)}
                  className={btnPrimary}
                >
                  {s.cta} <ArrowRight size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FormationAnciensEleves({ onSelectIntent }) {
  return (
    <section id="anciens-eleves" data-testid="section-formation-anciens" className="py-24 sm:py-32 bg-sand/85 dark:bg-onyx/85">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
        <SectionTitle
          eyebrow="Anciens élèves"
          title="Vous avez déjà suivi la formation ?"
          lead="Votre parcours peut continuer. Selon votre formation et votre expérience, vous pouvez demander votre agrément ou candidater à un niveau supérieur."
          center
        />
        <div className="flex flex-wrap justify-center gap-3">
          <button data-testid="anciens-cta-agrement" onClick={() => onSelectIntent("agrement")} className={btnPrimary}>
            Demander mon agrément
          </button>
          <button data-testid="anciens-cta-maitre" onClick={() => onSelectIntent("maitre-praticien")} className={btnSecondary}>
            Candidater Maître de Soi
          </button>
          <button data-testid="anciens-cta-question" onClick={() => onSelectIntent("licence-info")} className={btnSecondary}>
            Poser une question
          </button>
        </div>
      </div>
    </section>
  );
}

function FormationFormateur({ onSelectIntent }) {
  return (
    <section data-testid="section-formation-formateur" className="py-20 bg-ivory/85 dark:bg-night/85">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
        <div className="flex items-center justify-center gap-3 mb-5">
          <span className="h-px w-10 bg-gold" />
          <span className="text-[11px] tracking-[0.3em] uppercase text-stone dark:text-mist">Évolution possible</span>
          <span className="h-px w-10 bg-gold" />
        </div>
        <h2 className="font-serif font-light text-3xl sm:text-4xl text-ink dark:text-whisper leading-tight">
          Une évolution possible : Formateur agréé
        </h2>
        <p className="mt-6 text-stone dark:text-mist text-base sm:text-lg leading-relaxed">
          Certains Maîtres de Soi pourront, après sélection et validation, candidater à un parcours spécifique de Formateur agréé.
        </p>
        <button
          data-testid="formateur-cta"
          onClick={() => onSelectIntent("formateur-info")}
          className={`${btnSecondary} mt-8`}
        >
          En savoir plus
        </button>
      </div>
    </section>
  );
}

function FormationCandidatureForm({ defaultIntent, formRef }) {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    current_status: FORMATION_STATUSES[0].value,
    intent: defaultIntent || FORMATION_INTENTS[0].value,
    message: "",
    consent: false,
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  useEffect(() => {
    if (defaultIntent) setForm((f) => ({ ...f, intent: defaultIntent }));
  }, [defaultIntent]);

  const change = (k) => (e) => setForm((f) => ({
    ...f,
    [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email) {
      setStatus({ state: "err", msg: "Merci de renseigner prénom, nom et email." });
      return;
    }
    if (!form.consent) {
      setStatus({ state: "err", msg: "Merci d'accepter le traitement de votre demande." });
      return;
    }
    setStatus({ state: "sending", msg: "Envoi en cours…" });
    try {
      const statusLabel = FORMATION_STATUSES.find((s) => s.value === form.current_status)?.label || form.current_status;
      const intentLabel = FORMATION_INTENTS.find((s) => s.value === form.intent)?.label || form.intent;

      const [emailResult, apiResult] = await Promise.allSettled([
        sendWeb3Form({
          subject: `[Le guérisseur] Candidature Formation — ${form.first_name} ${form.last_name}`,
          fromName: `Le guérisseur — Formation`,
          message:
            `Nouvelle candidature Formation & Licences\n\n` +
            `Prénom  : ${form.first_name}\n` +
            `Nom     : ${form.last_name}\n` +
            `Email   : ${form.email}\n` +
            `Tél.    : ${form.phone || "—"}\n` +
            `Statut  : ${statusLabel}\n` +
            `Souhait : ${intentLabel}\n\n` +
            `Message :\n${form.message || "—"}\n`,
          replyTo: form.email,
        }),
        axios.post(`${API}/formation/applications`, form),
      ]);
      const persisted = apiResult.status === "fulfilled";
      if (!persisted) throw new Error("Persistence failed");
      setStatus({ state: "ok", msg: "Merci. Votre demande a bien été reçue. Nous reviendrons vers vous prochainement." });
      setForm({
        first_name: "", last_name: "", email: "", phone: "",
        current_status: FORMATION_STATUSES[0].value,
        intent: FORMATION_INTENTS[0].value,
        message: "", consent: false,
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Une erreur est survenue. Réessayez plus tard.";
      setStatus({ state: "err", msg });
    }
  };

  const inputCls = "w-full bg-ivory dark:bg-night border border-beige dark:border-charcoal rounded-xl px-5 py-3.5 text-ink dark:text-whisper focus:outline-none focus:border-forest dark:focus:border-gold transition-colors";

  return (
    <section id="candidature" ref={formRef} data-testid="section-formation-candidature" className="py-24 sm:py-32 bg-sand/85 dark:bg-onyx/85">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <SectionTitle eyebrow="Candidature" title="Envoyer ma candidature" lead="Chaque candidature est étudiée avec attention." center />

        <form onSubmit={submit} data-testid="formation-form" className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-2">Prénom *</label>
              <input data-testid="formation-first-name" value={form.first_name} onChange={change("first_name")} className={inputCls} />
            </div>
            <div>
              <label className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-2">Nom *</label>
              <input data-testid="formation-last-name" value={form.last_name} onChange={change("last_name")} className={inputCls} />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-2">Email *</label>
              <input data-testid="formation-email" type="email" value={form.email} onChange={change("email")} className={inputCls} />
            </div>
            <div>
              <label className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-2">Téléphone</label>
              <input data-testid="formation-phone" type="tel" value={form.phone} onChange={change("phone")} className={inputCls} />
            </div>
          </div>
          <div>
            <label className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-2">Statut actuel</label>
            <select data-testid="formation-current-status" value={form.current_status} onChange={change("current_status")} className={inputCls}>
              {FORMATION_STATUSES.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-2">Je souhaite</label>
            <select data-testid="formation-intent" value={form.intent} onChange={change("intent")} className={inputCls}>
              {FORMATION_INTENTS.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] tracking-[0.2em] uppercase text-stone dark:text-mist mb-2">Message</label>
            <textarea data-testid="formation-message" rows={5} value={form.message} onChange={change("message")} className={`${inputCls} resize-none`} />
          </div>
          <label className="flex items-start gap-3 text-sm text-stone dark:text-mist">
            <input data-testid="formation-consent" type="checkbox" checked={form.consent} onChange={change("consent")} className="h-4 w-4 mt-0.5 accent-forest shrink-0" />
            <span>J'accepte que ma demande soit traitée dans le cadre de ma candidature. Aucune revente de données.</span>
          </label>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
            <button
              type="submit"
              data-testid="formation-submit"
              disabled={status.state === "sending"}
              className={`${btnPrimary} disabled:opacity-50`}
            >
              <Send size={14} /> {status.state === "sending" ? "Envoi…" : "Envoyer ma candidature"}
            </button>
            {status.state !== "idle" && (
              <p data-testid="formation-status" className={`text-sm ${status.state === "ok" ? "text-forest dark:text-gold" : status.state === "err" ? "text-red-600 dark:text-red-400" : "text-stone dark:text-mist"}`}>
                {status.msg}
              </p>
            )}
          </div>
        </form>

        <p className="mt-10 text-xs text-stone dark:text-mist italic leading-relaxed text-center max-w-2xl mx-auto">
          Les certifications et agréments présentés ne constituent pas une qualification médicale ou paramédicale
          et ne donnent pas le droit d'exercer une profession réglementée.
        </p>
      </div>
    </section>
  );
}

// ============== LES QUATRE UNIVERS (accueil) ==============
const PARCOURS_CARDS = [
  {
    to: "/soins", title: "Être accompagné", icon: Heart,
    desc: "Des accompagnements individuels, notamment à distance, pour expérimenter mon approche dans un cadre personnalisé.",
    cta: "Découvrir les accompagnements",
  },
  {
    to: "/ressources", title: "Expérimenter et apprendre", icon: Sparkles,
    desc: "Lives, pratiques, mini-formations, exercices et parcours permettant de découvrir progressivement mon univers et d'expérimenter certaines pratiques par soi-même.",
    cta: "Découvrir les enseignements",
  },
  {
    to: "/formation", title: "Devenir praticien", icon: GraduationCap,
    desc: "Une formation structurée pour les personnes qui souhaitent approfondir l'Énergétique de la Conscience et apprendre à accompagner d'autres personnes dans un cadre professionnel, éthique et clairement défini.",
    cta: "Découvrir la formation",
  },
];

function Parcours() {
  return (
    <section id="parcours" data-testid="section-parcours" className="py-24 sm:py-32 bg-ivory/85 dark:bg-night/85">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <SectionTitle
          eyebrow="Trois parcours"
          title="Que recherchez-vous aujourd'hui ?"
          center
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {PARCOURS_CARDS.map((u, i) => (
            <Reveal key={u.title} delay={i * 0.08} y={24} className="h-full">
              <article data-testid={`parcours-card-${i}`} className="group h-full flex flex-col rounded-2xl border border-beige dark:border-charcoal bg-sand/60 dark:bg-onyx/60 p-7 sm:p-8 transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1.5 hover:border-gold/60 dark:hover:border-gold/50 hover:shadow-[0_24px_60px_-24px_rgba(45,74,62,0.35)]">
                <u.icon size={26} strokeWidth={1.25} className="text-forest dark:text-gold mb-5 transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6" />
                <h3 className="font-serif text-2xl text-ink dark:text-whisper leading-tight">{u.title}</h3>
                <p className="mt-4 text-sm text-stone dark:text-mist leading-relaxed flex-1">{u.desc}</p>
                <div className="mt-6">
                  <Link data-testid={`parcours-cta-${i}`} to={u.to} className="inline-flex items-center gap-2 text-[11px] tracking-[0.16em] uppercase font-medium text-forest dark:text-gold link-underline">
                    {u.cta} <ArrowRight size={12} />
                  </Link>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============== MES UNIVERS (accueil — univers complémentaires de Denis Tran) ==============
const UNIVERS_COMPLEMENTAIRES = [
  {
    href: "https://lavoielibredutao.fr", title: "La Voie Libre du Tao", icon: Compass,
    desc: "Un chemin d'étude, de pratique et de transformation inspiré du Tao.",
    cta: "Découvrir La Voie Libre du Tao",
  },
  {
    href: "https://taodes4c.fr", title: "Le Tao des 4C", sub: "Corps • Cœur • Cerveau • Conscience", icon: Layers,
    desc: "Une méthode et des expériences interactives pour explorer son propre chemin à travers quatre dimensions.",
    cta: "Entrer dans le Tao des 4C",
  },
];

function MesUnivers() {
  return (
    <section id="mes-univers" data-testid="section-mes-univers" className="py-24 sm:py-32 bg-ivory/85 dark:bg-night/85">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <SectionTitle
          eyebrow="Mes univers"
          title="Mes univers"
          lead="Au-delà de mes accompagnements et formations, j'ai également développé deux univers complémentaires autour du Tao et de la transformation personnelle."
          center
        />
        <div className="grid sm:grid-cols-2 gap-5 lg:gap-6 max-w-4xl mx-auto">
          {UNIVERS_COMPLEMENTAIRES.map((u, i) => (
            <Reveal key={u.title} delay={i * 0.08} y={24} className="h-full">
              <article data-testid={`mes-univers-card-${i}`} className="group h-full flex flex-col rounded-2xl border border-beige dark:border-charcoal bg-sand/60 dark:bg-onyx/60 p-7 sm:p-8 transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1.5 hover:border-gold/60 dark:hover:border-gold/50 hover:shadow-[0_24px_60px_-24px_rgba(45,74,62,0.35)]">
                <u.icon size={26} strokeWidth={1.25} className="text-forest dark:text-gold mb-5 transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6" />
                <h3 className="font-serif text-2xl text-ink dark:text-whisper leading-tight">{u.title}</h3>
                {u.sub && <p className="mt-1.5 text-[11px] tracking-[0.16em] uppercase text-stone dark:text-mist">{u.sub}</p>}
                <p className="mt-4 text-sm text-stone dark:text-mist leading-relaxed flex-1">{u.desc}</p>
                <div className="mt-6">
                  <a data-testid={`mes-univers-cta-${i}`} href={u.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[11px] tracking-[0.16em] uppercase font-medium text-forest dark:text-gold link-underline">
                    {u.cta} <ExternalLink size={12} />
                  </a>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============== TOURNÉE TEASER (accueil → /tournee) ==============
function TourneeTeaser() {
  return (
    <section id="tournee-teaser" data-testid="section-tournee-teaser" className="py-24 sm:py-32 bg-sand/60 dark:bg-onyx/60">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
        <SectionTitle
          eyebrow="Sur la route — janvier à juin"
          title="La Tournée LE GUÉRISSEUR"
          lead="Soirées de rencontre, soins et journées de formation à travers la France, la Suisse et le Luxembourg : départ dans le Sud (Perpignan, Narbonne, Montpellier, Marseille, Toulon, Nice), puis Toulouse, Bordeaux, Lyon, Genève, Strasbourg, Metz et Luxembourg. Calendrier provisoire — les dates sont confirmées selon le nombre d'inscrits par ville."
          center
        />
        <Reveal delay={0.15}>
          <div className="flex flex-wrap justify-center gap-3">
            <Link data-testid="teaser-cta-tournee" to="/tournee" className={btnPrimary}>
              Découvrir la tournée <ArrowRight size={14} />
            </Link>
            <Link data-testid="teaser-cta-participer" to="/tournee#participer" className={btnSecondary}>
              Je veux participer
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ============== PAGES DES QUATRE UNIVERS ==============
// En-tête de page sobre partagé (réutilise MaskedLine / Reveal)
function PageHero({ eyebrow, title, subtitle, children }) {
  return (
    <div className="relative pt-36 pb-16 sm:pt-44 sm:pb-20 text-center overflow-hidden">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <Reveal className="flex items-center justify-center gap-3 mb-8">
          <span className="h-px w-12 bg-gold" />
          <span className="text-[11px] tracking-[0.35em] uppercase text-stone dark:text-mist">{eyebrow}</span>
          <span className="h-px w-12 bg-gold" />
        </Reveal>
        <h1 data-testid="page-h1" className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.12] text-ink dark:text-whisper">
          <MaskedLine delay={0.05}>{title}</MaskedLine>
        </h1>
        {subtitle && (
          <Reveal delay={0.2}>
            <p className="mt-6 font-serif text-xl sm:text-2xl italic text-forest dark:text-gold leading-snug">{subtitle}</p>
          </Reveal>
        )}
        {children}
      </div>
    </div>
  );
}

// ---- /soins : soins individuels à distance ----
const SOINS_ETAPES = [
  "Échange préalable",
  "Clarification de l'objectif",
  "Séance individuelle à distance",
  "Pratique adaptée",
  "Temps d'intégration",
  "Conseils de pratique personnelle",
];

function SoinsPage() {
  return (
    <div data-testid="page-soins">
      <PageHero eyebrow="Être accompagné" title="Soins individuels à distance" subtitle="Un temps pour vous, où que vous soyez." />
      <div className="bg-ivory/85 dark:bg-night/85">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-24 sm:py-28">
          <SectionTitle eyebrow="Le déroulé" title="Comment se déroule une séance" center />
          <ol className="border-t border-beige dark:border-charcoal">
            {SOINS_ETAPES.map((etape, i) => (
              <Reveal key={etape} delay={i * 0.05} y={16}>
                <li data-testid={`soins-etape-${i + 1}`} className="flex items-baseline gap-6 border-b border-beige dark:border-charcoal py-5">
                  <span className="font-serif text-2xl text-gold/80 w-10 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-serif text-xl sm:text-2xl text-ink dark:text-whisper">{etape}</span>
                </li>
              </Reveal>
            ))}
          </ol>

          <Reveal delay={0.1}>
            <div data-testid="soins-offre" className="mt-16 rounded-2xl border border-gold/60 bg-sand/95 dark:bg-onyx/95 p-8 sm:p-10 text-center shadow-xl shadow-gold/10">
              <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-3">Offre principale</p>
              <h2 className="font-serif text-3xl sm:text-4xl text-ink dark:text-whisper">Séance individuelle</h2>
              <p className="mt-3 text-sm text-stone dark:text-mist">À distance · Durée : 30 à 60 minutes</p>
              <p className="mt-6 text-[11px] tracking-[0.3em] uppercase text-stone dark:text-mist">À partir de</p>
              <p data-testid="soins-prix" className="mt-1 font-serif text-6xl sm:text-7xl text-forest dark:text-gold leading-none">
                {SESSION_PRICE_EUR} <span className="text-3xl sm:text-4xl align-baseline">€</span>
              </p>
              <div>
                <a data-testid="soins-cta-reserver" href="https://tidycal.com/denistran" target="_blank" rel="noopener noreferrer" className={`${btnPrimary} mt-8`}>
                  Réserver ma séance <ArrowRight size={14} />
                </a>
              </div>
              <p data-testid="soins-legal" className="mt-8 text-xs text-stone dark:text-mist italic leading-relaxed max-w-xl mx-auto">
                Les pratiques proposées relèvent d'une démarche de bien-être, de relaxation, de développement personnel et d'accompagnement. Elles ne constituent pas un diagnostic médical et ne remplacent pas une consultation auprès d'un professionnel de santé.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
      <SoinsVideos
        tid="soins-page"
        sectionClass="py-24 sm:py-32 bg-sand/60 dark:bg-onyx/60"
        eyebrow="Vidéos"
        title="Les soins en direct et à distance, en vidéo"
        lead="Parcourez ma playlist directement dans le lecteur : soins en direct, explications et démonstrations."
      />
      <Reviews
        sectionId=""
        tid="soins-"
        eyebrow="Témoignages"
        title="Ils ont été accompagnés"
        lead="Les mots de celles et ceux qui ont reçu un accompagnement à distance."
        withForm={false}
        bgClass="bg-ivory/85 dark:bg-night/85"
      />
    </div>
  );
}

// ---- /energetique-de-la-conscience : la méthode ----
const FONDEMENTS = ["Qi Gong", "MTC", "Tao", "Méditation", "Massage", "Pratiques énergétiques", "Observation du corps", "Travail de conscience"];
const QUATRE_C = ["Corps", "Cœur", "Cerveau", "Conscience"];

function EnergetiquePage() {
  return (
    <div data-testid="page-energetique">
      <PageHero eyebrow="Apprendre" title="Énergétique de la Conscience" subtitle="Une approche qui relie le corps, l'énergie et la conscience." />
      <div className="bg-ivory/85 dark:bg-night/85">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24 sm:py-28">
          <SectionTitle eyebrow="Les fondements" title="Une approche enracinée" lead="Une pratique nourrie par plusieurs traditions, transmise avec exigence." center />
          <Reveal>
            <div data-testid="energetique-fondements" className="flex flex-wrap justify-center gap-2.5">
              {FONDEMENTS.map((f) => (
                <span key={f} className="text-[10px] sm:text-[11px] tracking-[0.14em] uppercase border border-beige dark:border-charcoal bg-sand/70 dark:bg-onyx/70 text-stone dark:text-mist rounded-full px-4 py-2">
                  {f}
                </span>
              ))}
            </div>
          </Reveal>

          <SectionTitle eyebrow="Le modèle" title="Les 4C" lead="Les quatre dimensions de l'être." center />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {QUATRE_C.map((c, i) => (
              <Reveal key={c} delay={i * 0.07}>
                <div data-testid={`quatre-c-${i}`} className="rounded-2xl border border-beige dark:border-charcoal bg-sand/60 dark:bg-onyx/60 p-6 sm:p-8 text-center h-full">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-gold mb-2">{String(i + 1).padStart(2, "0")}</p>
                  <p className="font-serif text-2xl sm:text-3xl text-ink dark:text-whisper">{c}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <div className="mt-14 flex flex-wrap justify-center gap-3">
              <Link data-testid="energetique-cta-formation" to="/formation-energetique-de-la-conscience" className={btnPrimary}>
                Découvrir la formation <ArrowRight size={14} />
              </Link>
              <a data-testid="energetique-cta-site" href={ENERGETIQUE_URL} target="_blank" rel="noopener noreferrer" className={btnSecondary}>
                energeticienconscience.fr <ExternalLink size={13} />
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}

// ---- /ressources : contenus complémentaires (ex-page La Voie Libre du Tao) ----
function RessourcesPage() {
  const TRANSMISSIONS = [
    { icon: BookOpen, t: "Écrits & e-books", d: "Livres et e-books consacrés au Tao et à la philosophie taoïste, publiés chez Leduc Éditions et sur Amazon.", href: "https://www.amazon.com/dp/B0HJP58YQ8", cta: "Découvrir les écrits" },
    { icon: Youtube, t: "Enseignement en vidéo", d: "La chaîne YouTube du Guérisseur : lives, soins en direct et partages autour de la pratique.", href: "https://www.youtube.com/@le-guerisseur", cta: "Voir la chaîne" },
    { icon: FileText, t: "Articles & webinaires", d: "Des contenus gratuits pour découvrir la pensée taoïste et la pratique énergétique, pas à pas.", href: "#contenus", cta: "Lire les contenus" },
  ];
  return (
    <div data-testid="page-ressources">
      <PageHero eyebrow="Ressources" title="Ressources complémentaires" subtitle="Écrits, vidéos, contenus et créations pour prolonger votre exploration.">
        <Reveal delay={0.35}>
          <div className="mt-10">
            <a data-testid="voie-libre-cta" href="#programme" className={btnPrimary}>
              Découvrir le programme <ArrowRight size={14} />
            </a>
          </div>
        </Reveal>
      </PageHero>
      <div id="programme" className="bg-ivory/85 dark:bg-night/85">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24 sm:py-28">
          <SectionTitle eyebrow="Le programme" title="Les transmissions" lead="Les contenus déjà disponibles pour cheminer avec le Tao." center />
          <div className="grid sm:grid-cols-3 gap-5 lg:gap-6">
            {TRANSMISSIONS.map((t, i) => (
              <Reveal key={t.t} delay={i * 0.08} y={24} className="h-full">
                <article data-testid={`voie-card-${i}`} className="group h-full flex flex-col rounded-2xl border border-beige dark:border-charcoal bg-sand/60 dark:bg-onyx/60 p-7 sm:p-8 transition-[transform,border-color] duration-500 hover:-translate-y-1.5 hover:border-gold/60">
                  <t.icon size={26} strokeWidth={1.25} className="text-forest dark:text-gold mb-5" />
                  <h3 className="font-serif text-2xl text-ink dark:text-whisper">{t.t}</h3>
                  <p className="mt-3 text-sm text-stone dark:text-mist leading-relaxed flex-1">{t.d}</p>
                  <a data-testid={`voie-cta-${i}`} href={t.href} target={t.href.startsWith("http") ? "_blank" : undefined} rel={t.href.startsWith("http") ? "noopener noreferrer" : undefined} className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.16em] uppercase font-medium text-forest dark:text-gold link-underline self-start">
                    {t.cta} {t.href.startsWith("http") ? <ExternalLink size={12} /> : <ArrowRight size={12} />}
                  </a>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
      <YouTubeSection />
      <Contenus />
      <Oracle />
    </div>
  );
}

// ---- /tao-des-4c : univers 4C (lien externe volontairement désactivé) ----
// Bouton 4C : lien externe si TAO4C_ENABLED, sinon pastille « Bientôt disponible »
function T4CButton({ testid }) {
  if (TAO4C_ENABLED) {
    return (
      <a data-testid={testid} href={TAO4C_URL} target="_blank" rel="noopener noreferrer" className={btnSecondary}>
        Découvrir <ExternalLink size={13} />
      </a>
    );
  }
  return (
    <span data-testid={testid} aria-disabled="true" className="inline-flex items-center gap-2 rounded-full border border-dashed border-beige dark:border-charcoal text-stone/70 dark:text-mist/70 px-5 py-2.5 text-[11px] tracking-[0.16em] uppercase cursor-not-allowed select-none">
      Bientôt disponible
    </span>
  );
}

function TaoQuatreCPage() {
  const BLOCS = [
    { id: "carnets", t: "Les Carnets", d: "Des carnets pour cheminer avec les 4C, à votre rythme." },
    { id: "jeu", t: "Le Jeu", d: "Un jeu pour expérimenter les quatre dimensions de l'être." },
    { id: "experience", t: "L'Expérience numérique", d: "Une expérience en ligne pour découvrir l'univers des 4C." },
  ];
  return (
    <div data-testid="page-tao-4c">
      <PageHero eyebrow="Expérimenter" title="Le Tao des 4C" subtitle="Corps • Cœur • Cerveau • Conscience" />
      <div id="quatre-c" className="bg-ivory/85 dark:bg-night/85">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-24 sm:py-28">
          <SectionTitle eyebrow="Le modèle" title="Les quatre dimensions" center />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-20">
            {QUATRE_C.map((c, i) => (
              <Reveal key={c} delay={i * 0.07}>
                <div data-testid={`tao4c-dim-${i}`} className="rounded-2xl border border-beige dark:border-charcoal bg-sand/60 dark:bg-onyx/60 p-6 sm:p-8 text-center h-full">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-gold mb-2">{String(i + 1).padStart(2, "0")}</p>
                  <p className="font-serif text-2xl sm:text-3xl text-ink dark:text-whisper">{c}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div data-testid="tao4c-explication" className="max-w-3xl mx-auto text-center mb-20 space-y-5 text-stone dark:text-mist leading-relaxed text-base sm:text-lg">
              <p>
                Née d'années de pratique, d'enseignement et d'accompagnement, la Méthode des 4C observe quatre dimensions
                de l'être — <span className="text-ink dark:text-whisper">Corps, Cœur, Cerveau et Conscience</span> — pour
                passer de la compréhension à l'expérimentation.
              </p>
              <p>
                Le Tao des 4C décline cette approche en carnets, jeu et expériences interactives, pour explorer
                son propre chemin à son rythme.
              </p>
              <a data-testid="tao4c-lien-site" href={TAO4C_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[11px] tracking-[0.16em] uppercase font-medium text-forest dark:text-gold link-underline">
                Explorer l'approche sur taodes4c.fr <ExternalLink size={12} />
              </a>
            </div>
          </Reveal>
          <SectionTitle eyebrow="L'univers" title="Carnets, jeu et expérience" center />
          <div className="grid sm:grid-cols-3 gap-5 lg:gap-6">
            {BLOCS.map((b, i) => (
              <Reveal key={b.id} delay={i * 0.08} y={24} className="h-full">
                <article id={b.id} data-testid={`tao4c-bloc-${b.id}`} className="h-full flex flex-col rounded-2xl border border-dashed border-beige dark:border-charcoal bg-sand/40 dark:bg-onyx/40 p-7 sm:p-8">
                  <h3 className="font-serif text-2xl text-ink dark:text-whisper">{b.t}</h3>
                  <p className="mt-3 text-sm text-stone dark:text-mist leading-relaxed flex-1">{b.d}</p>
                  <div className="mt-6"><T4CButton testid={`tao4c-cta-${b.id}`} /></div>
                </article>
              </Reveal>
            ))}
          </div>
          <p className="mt-12 text-center text-xs text-stone dark:text-mist italic">
            L'univers complet du Tao des 4C vous attend sur taodes4c.fr.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---- /reseaux-sociaux : porte d'entrée des réseaux ----
function ReseauxSociauxPage() {
  const CHOIX = [
    { want: "Je veux être accompagné", target: "Soins individuels", to: "/soins", icon: Heart },
    { want: "Je veux apprendre", target: "Énergétique de la Conscience", to: "/energetique-de-la-conscience", icon: Sparkles },
    { want: "Je veux explorer le Tao", target: "La Voie Libre du Tao", to: "https://lavoielibredutao.fr", icon: Compass, external: true },
    { want: "Je veux expérimenter les 4C", target: "Le Tao des 4C", to: "/tao-des-4c", icon: Layers },
  ];
  return (
    <div data-testid="page-reseaux-sociaux">
      <PageHero eyebrow="Bienvenue" title="Vous me découvrez sur les réseaux sociaux ?" subtitle="Voici par où commencer.">
        <Reveal delay={0.3}>
          <p className="mt-6 text-base sm:text-lg text-stone dark:text-mist">Bienvenue dans l'univers du Guérisseur.</p>
        </Reveal>
      </PageHero>
      <div className="bg-ivory/85 dark:bg-night/85">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24 sm:py-28">
          <div className="grid sm:grid-cols-2 gap-5 lg:gap-6">
            {CHOIX.map((c, i) => {
              const cardClass = "group h-full flex flex-col rounded-2xl border border-beige dark:border-charcoal bg-sand/60 dark:bg-onyx/60 p-7 sm:p-8 transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1.5 hover:border-gold/60 dark:hover:border-gold/50 hover:shadow-[0_24px_60px_-24px_rgba(45,74,62,0.35)]";
              const inner = (
                <>
                  <c.icon size={26} strokeWidth={1.25} className="text-forest dark:text-gold mb-5 transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6" />
                  <p className="text-[10px] tracking-[0.28em] uppercase text-stone dark:text-mist">{c.want}</p>
                  <p className="mt-2 font-serif text-2xl text-ink dark:text-whisper leading-tight flex-1">{c.target}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.16em] uppercase font-medium text-forest dark:text-gold">
                    Découvrir {c.external ? <ExternalLink size={12} /> : <ArrowRight size={12} className="transition-transform duration-300 group-hover:translate-x-1" />}
                  </span>
                </>
              );
              return (
                <Reveal key={c.want} delay={i * 0.07} y={22} className="h-full">
                  {c.external ? (
                    <a href={c.to} target="_blank" rel="noopener noreferrer" data-testid={`reseaux-choix-${i}`} className={cardClass}>
                      {inner}
                    </a>
                  ) : (
                    <Link to={c.to} data-testid={`reseaux-choix-${i}`} className={cardClass}>
                      {inner}
                    </Link>
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============== SOINS SPIRITUELS — SECTION PREMIUM (page /formation) ==============

// Smooth scroll helper routed through Lenis when available (fallback: native)
function smoothScrollTo(target) {
  const el = typeof target === "string" ? document.querySelector(target) : target;
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -84, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth", block: "start" });
}

// Kinetic masked line reveal — signature editorial moment.
// NOTE: whileInView must observe the OUTER wrapper — the inner span is fully clipped
// by overflow-hidden at its initial offset, so an observer on it would never fire.
function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <motion.span
      className={`block overflow-hidden pb-[0.14em] -mb-[0.14em] ${className}`}
      initial="hidden"
      whileInView="shown"
      viewport={{ once: true, margin: "-12%" }}
    >
      <motion.span
        className="block will-change-transform"
        variants={{ hidden: { y: "115%" }, shown: { y: "0%" } }}
        transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.span>
    </motion.span>
  );
}

// Scroll reveal wrapper (fade + rise, once)
function Reveal({ children, delay = 0, className = "", y = 28 }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

// Giant 氣 (Qi) watermark with slow parallax drift + breathing scale
function QiWatermark() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [90, -110]);
  return (
    <div ref={ref} aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden flex items-start justify-center pt-6">
      <motion.span style={{ y }} className="qi-breath font-serif leading-none select-none text-[16rem] sm:text-[24rem] lg:text-[30rem] text-forest/[0.05] dark:text-gold/[0.06]">
        氣
      </motion.span>
    </div>
  );
}

function SoinsHero({ onDecouvrir }) {
  return (
    <div className="relative pt-24 sm:pt-32 pb-20 sm:pb-28 overflow-hidden">
      <QiWatermark />
      <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
        <Reveal className="flex items-center justify-center gap-3 mb-10">
          <span className="h-px w-12 bg-gold" />
          <span data-testid="soins-eyebrow" className="text-[11px] tracking-[0.35em] uppercase text-stone dark:text-mist">L'approche originale</span>
          <span className="h-px w-12 bg-gold" />
        </Reveal>
        <h2 data-testid="soins-hero-title" className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.12] text-ink dark:text-whisper">
          <MaskedLine delay={0.05}>Énergéticien</MaskedLine>
          <MaskedLine delay={0.2}><em className="text-forest dark:text-gold">de la Conscience<sup className="text-[0.5em] align-super">®</sup></em></MaskedLine>
        </h2>
        <Reveal delay={0.35}>
          <p data-testid="soins-accroche" className="mt-8 font-serif text-2xl sm:text-3xl italic text-forest dark:text-gold leading-snug">
            Apprendre à accompagner la douleur à distance avec la Méthode des 4C
          </p>
        </Reveal>
        <Reveal delay={0.5}>
          <div data-testid="soins-definition" className="mt-8 max-w-2xl mx-auto text-sm sm:text-base text-stone dark:text-mist leading-relaxed space-y-4">
            <p>
              Découvrez une approche énergétique qui explore les interactions entre le corps, l'énergie, les émotions, le souffle, le mental et la conscience.
            </p>
            <p>
              La méthode apprend à accompagner une personne sans contact physique, avec son consentement, que la pratique soit réalisée à proximité ou à distance.
            </p>
            <p className="text-ink dark:text-whisper">Au cœur de cette approche : les 4C.</p>
            <p className="font-serif text-lg sm:text-xl tracking-[0.14em] uppercase text-forest dark:text-gold">
              Corps • Cœur • Cerveau • Conscience
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.65}>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <button data-testid="soins-cta-decouvrir" onClick={onDecouvrir} className={btnPrimary}>
              Découvrir la formation <ArrowRight size={14} />
            </button>
            <button data-testid="soins-cta-dates" onClick={() => smoothScrollTo("#dates")} className={btnSecondary}>
              Prochaines dates
            </button>
          </div>
        </Reveal>
      </div>
    </div>
  );
}

const AXES = [
  { icon: Radio, t: "Travail énergétique à distance", d: "Accompagner une personne sans contact physique, avec son consentement." },
  { icon: Layers, t: "Jing • Qi • Shen", d: "Les trois axes fondamentaux du Tao : essence, énergie vitale, esprit." },
  { icon: Compass, t: "Approche multidimensionnelle", d: "Une lecture globale de l'être humain : corps, énergie, émotions, souffle et conscience." },
  { icon: Globe, t: "Approche multiculturelle", d: "Qi Gong, Qi Gong médical, Médecine Traditionnelle Chinoise, Taoïsme et traditions bouddhistes." },
  { icon: BookOpen, t: "Cas concrets", d: "L'expérimentation au cœur de l'apprentissage, à travers des situations réelles." },
  { icon: Sparkles, t: "Pédagogie ludique & interactive", d: "Une transmission vivante et interactive, accessible à toutes et à tous." },
];

function NotreApproche() {
  return (
    <div className="py-24 sm:py-32">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <SectionTitle
          eyebrow="Les axes de la formation"
          title="Six axes, une pratique vivante"
          lead="La Méthode des 4C s'appuie sur six axes complémentaires qui permettent de passer de la compréhension à l'expérimentation."
          center
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {AXES.map((a, i) => (
            <Reveal key={a.t} delay={(i % 3) * 0.08} y={24} className="h-full">
              <div
                data-testid={`soins-approche-card-${i}`}
                className="group h-full rounded-2xl border border-beige dark:border-charcoal bg-sand/60 dark:bg-onyx/60 backdrop-blur-sm p-7 sm:p-8 transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1.5 hover:border-gold/60 dark:hover:border-gold/50 hover:shadow-[0_24px_60px_-24px_rgba(45,74,62,0.35)]"
              >
                <a.icon size={26} strokeWidth={1.25} className="text-forest dark:text-gold mb-5 transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6" />
                <h3 className="font-serif text-xl sm:text-2xl text-ink dark:text-whisper mb-2.5">{a.t}</h3>
                <p className="text-sm text-stone dark:text-mist leading-relaxed">{a.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}

const MARQUEE_WORDS = ["Devenir son propre guide", "Accompagner l'autre en conscience"];

// Slow editorial marquee — the signature tagline in infinite drift
function QiMarquee() {
  const row = (key) => (
    <div key={key} className="flex shrink-0 items-center">
      {MARQUEE_WORDS.map((w) => (
        <span key={w} className="flex items-center">
          <span className="font-serif italic whitespace-nowrap text-3xl sm:text-4xl lg:text-5xl text-forest/90 dark:text-gold/90 px-6 sm:px-8">{w}</span>
          <span className="text-gold/70 dark:text-gold/60 text-xl sm:text-2xl">→</span>
        </span>
      ))}
    </div>
  );
  return (
    <div data-testid="soins-marquee" aria-hidden="true" className="relative overflow-hidden border-y border-beige/70 dark:border-charcoal/70 py-5 sm:py-7">
      <div className="qi-marquee-track flex w-max">{row("a")}{row("b")}</div>
    </div>
  );
}

const PARCOURS = [
  { n: "01", t: "Fondations énergétiques", d: "Ancrage, écoute, attention consciente et cadre de pratique.", e: "Apprendre à revenir dans son corps, stabiliser son attention et développer une présence consciente avant toute pratique d'accompagnement." },
  { n: "02", t: "Qi & perception énergétique", d: "Principes du Qi, Jing • Qi • Shen et expérimentation du ressenti.", e: "Développer le ressenti énergétique et apprendre à distinguer perception, sensation et interprétation." },
  { n: "03", t: "Conscience & harmonisation", d: "Corps, souffle, émotions, mental et conscience.", e: "Découvrir les quatre dimensions des 4C et apprendre à les utiliser comme grille d'observation et d'accompagnement." },
  { n: "04", t: "Accompagnement à distance & intégration", d: "Cas concrets, pratique complète et mise en situation.", e: "Mettre en pratique le protocole des 4C dans l'accompagnement énergétique sans contact, notamment à distance." },
];

function ParcoursFormation({ onDecouvrir }) {
  return (
    <div className="py-24 sm:py-32">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <Reveal>
          <div data-testid="soins-parcours" className="relative overflow-hidden rounded-[2rem] bg-forest dark:bg-charcoal px-6 py-14 sm:px-12 sm:py-20 lg:px-16 shadow-[0_40px_120px_-40px_rgba(26,33,30,0.5)]">
            <span aria-hidden="true" className="qi-breath absolute -right-12 -top-20 font-serif leading-none select-none text-[18rem] sm:text-[24rem] text-gold/10 pointer-events-none">氣</span>
            <div className="relative">
              <div className="flex items-center gap-3 mb-8">
                <span className="h-px w-10 bg-gold" />
                <span className="text-[11px] tracking-[0.35em] uppercase text-gold">La formation</span>
              </div>
              <h3 className="font-serif font-light text-4xl sm:text-5xl leading-[1.1] text-ivory max-w-2xl">
                Formation d'Énergéticien <em className="text-gold">de la Conscience<sup className="text-[0.5em] align-super">®</sup></em>
              </h3>

              <div className="mt-12 mb-14 flex flex-wrap items-center gap-x-12 gap-y-8">
                <div>
                  <p className="text-[11px] tracking-[0.3em] uppercase text-whisper/60 mb-3">Durée</p>
                  <p data-testid="soins-duration" className="font-serif text-6xl sm:text-7xl text-ivory leading-none">4 <span className="text-3xl sm:text-4xl text-gold align-baseline">jours</span></p>
                </div>
                <div className="h-16 w-px bg-gold/30 hidden sm:block" />
                <div>
                  <p className="text-[11px] tracking-[0.3em] uppercase text-whisper/60 mb-3">Investissement</p>
                  <p data-testid="soins-price" className="font-serif text-6xl sm:text-7xl text-ivory leading-none">999 <span className="text-3xl sm:text-4xl text-gold align-baseline">€</span></p>
                </div>
              </div>

              <ol className="border-t border-ivory/15">
                {PARCOURS.map((s, i) => (
                  <Reveal key={s.n} delay={i * 0.07} y={20}>
                    <li data-testid={`soins-step-${i + 1}`} className="group grid sm:grid-cols-[130px_1fr] lg:grid-cols-[150px_1fr_1fr] items-baseline gap-x-8 gap-y-1.5 border-b border-ivory/15 py-6 sm:py-7">
                      <span className="flex items-baseline gap-2.5">
                        <span className="text-[10px] tracking-[0.3em] uppercase text-whisper/50">Jour</span>
                        <span className="font-serif text-3xl sm:text-4xl text-gold/80 transition-colors duration-500 group-hover:text-gold">{s.n}</span>
                      </span>
                      <span className="font-serif text-xl sm:text-2xl text-ivory transition-transform duration-500 group-hover:translate-x-1.5">{s.t}</span>
                      <span className="text-sm text-whisper/70 leading-relaxed sm:col-start-2 lg:col-start-3">
                        {s.d}
                        <span className="block mt-2 text-whisper/55">{s.e}</span>
                      </span>
                    </li>
                  </Reveal>
                ))}
              </ol>

              <div className="mt-12">
                <button
                  data-testid="soins-cta-parcours"
                  onClick={onDecouvrir}
                  className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-[11px] font-semibold tracking-[0.25em] uppercase text-ink transition-[background-color,gap] duration-300 hover:bg-gold-bright hover:gap-3.5"
                >
                  Découvrir la formation <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}

const SOINS_FAQS = [
  {
    q: "Qu'est-ce qu'un soin spirituel ?",
    a: "Un soin spirituel est un temps d'harmonisation énergétique qui s'adresse à la dimension intérieure de la personne — corps, souffle, émotions et conscience. Il s'inscrit dans une démarche de bien-être et de reconnexion à soi, sans aucune visée médicale.",
  },
  {
    q: "Quelle est la différence entre un énergéticien et un Énergéticien de la Conscience ?",
    a: "L'Énergéticien de la Conscience suit une méthode structurée autour des 4C et d'une approche multidimensionnelle intégrant notamment Qi Gong, Qi Gong médical, Médecine Traditionnelle Chinoise, Taoïsme, méditation et travail énergétique à distance.",
  },
  {
    q: "Que fait concrètement un Énergéticien de la Conscience ?",
    a: "Il utilise les principes et pratiques transmis dans la formation pour accompagner une personne dans une démarche énergétique et de bien-être. La Méthode des 4C lui permet d'observer quatre dimensions : Corps, Cœur, Cerveau et Conscience.",
  },
  {
    q: "Les soins spirituels sont-ils réservés aux personnes spirituelles ?",
    a: "Non. Aucune croyance ni aucun parcours particulier ne sont requis. Le soin s'adresse à toute personne en quête d'apaisement, de recentrage ou d'exploration intérieure, quelle que soit sa sensibilité.",
  },
  {
    q: "Faut-il avoir des connaissances particulières pour suivre la formation ?",
    a: "Non. La formation est ouverte à toutes et à tous — débutants comme praticiens confirmés. Elle transmet pas à pas les fondamentaux : écoute, perception énergétique et posture d'accompagnement.",
  },
  {
    q: "La formation permet-elle de pratiquer professionnellement ?",
    a: "Oui, dans le champ du bien-être et de l'accompagnement personnel. Elle délivre un certificat de formation et la posture nécessaire pour accompagner en toute justesse, dans le respect du cadre légal : elle ne confère aucune qualification médicale ou paramédicale.",
  },
  {
    q: "Qui peut candidater à la certification, l'agrément ou la maîtrise ?",
    a: "Les anciens élèves de l'école, ainsi que les praticiens ayant déjà pratiqué une technique proche du Qi Gong médical, peuvent candidater. Selon votre parcours et votre expérience, vous pouvez demander la certification (Praticien certifié), l'agrément (Praticien agréé) ou la maîtrise (Maître de Soi). Chaque candidature est étudiée avec attention, puis validée par l'école.",
  },
  {
    q: "Qu'est-ce qu'un Maître de Soi ?",
    quote: "Le véritable maître n'est pas celui qui maîtrise les autres, mais celui qui apprend à se connaître, à se gouverner et à devenir libre intérieurement.",
    a: "Le Maître de Soi est une personne engagée dans une démarche d'autonomie intérieure, de conscience et de responsabilité. Il utilise les pratiques énergétiques et contemplatives comme des supports d'observation, d'harmonisation et de connaissance de soi, sans se substituer à la médecine, à la psychologie ou à tout autre professionnel de santé. Son rôle premier est de développer son propre discernement et de favoriser l'autonomie de ceux qu'il accompagne.",
  },
  {
    q: "Peut-on pratiquer à distance ?",
    a: "Oui. La formation enseigne un accompagnement énergétique sans contact physique. La pratique peut être réalisée à proximité ou à distance, dans le cadre et les limites enseignés pendant la formation.",
  },
  {
    q: "Peut-on recevoir un soin spirituel sans parler de ses problèmes personnels ?",
    a: "Absolument. Le soin ne nécessite aucune confidence : il suffit d'être présent et réceptif. Le travail énergétique se déroule dans le silence et le respect total de votre intimité.",
  },
  {
    q: "Les soins spirituels guérissent-ils les maladies ?",
    a: "Non. La formation relève du bien-être, de l'accompagnement personnel et des pratiques énergétiques et spirituelles. Elle ne constitue pas une formation médicale et ne permet pas de diagnostiquer ou traiter une maladie.",
  },
  {
    q: "Quelle est la philosophie de l'Énergéticien de la Conscience ?",
    a: "Énergie, conscience, autonomie. Une conviction simple : chaque personne porte en elle les ressources de sa propre transformation. Le praticien ne « guérit » pas — il crée les conditions d'une reconnexion à soi et favorise l'autonomie de ceux qu'il accompagne.",
  },
];

function SoinsFaq() {
  const [open, setOpen] = useState(null);
  return (
    <div className="pb-24 sm:pb-32">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <SectionTitle eyebrow="Questions" title="Questions fréquentes" center />
        <div data-testid="soins-faq" className="space-y-3">
          {SOINS_FAQS.map((f, i) => (
            <div
              key={i}
              data-testid={`soins-faq-item-${i}`}
              className="border border-beige dark:border-charcoal rounded-xl overflow-hidden bg-sand/60 dark:bg-onyx/60 backdrop-blur-sm transition-colors duration-300 hover:border-gold/50"
            >
              <button
                data-testid={`soins-faq-trigger-${i}`}
                aria-expanded={open === i}
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between gap-4 p-5 sm:p-6 text-left"
              >
                <span className="font-serif text-lg sm:text-xl text-ink dark:text-whisper">{f.q}</span>
                <motion.span
                  animate={{ rotate: open === i ? 45 : 0 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className="text-forest dark:text-gold shrink-0"
                >
                  <Plus size={18} />
                </motion.span>
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden"
                  >
                    {f.quote && (
                      <blockquote className="mx-5 sm:mx-6 mb-4 font-serif italic text-lg sm:text-xl text-forest dark:text-gold border-l-2 border-gold/60 pl-4 leading-relaxed">
                        « {f.quote} »
                      </blockquote>
                    )}
                    <p data-testid={`soins-faq-answer-${i}`} className="px-5 sm:px-6 pb-6 text-sm sm:text-base text-stone dark:text-mist leading-relaxed">
                      {f.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SoinsLegal() {
  return (
    <div className="pb-24 sm:pb-28">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <div className="gold-divider mb-10" />
        <p data-testid="soins-legal" className="text-center text-xs sm:text-sm text-stone dark:text-mist italic leading-relaxed">
          Cette formation relève du domaine du bien-être, de l'accompagnement personnel et des pratiques énergétiques et spirituelles. Elle ne constitue pas une formation médicale et ne permet pas de diagnostiquer ou traiter une maladie. Elle ne remplace pas un diagnostic, un traitement ou un suivi par un professionnel de santé.
        </p>
      </div>
    </div>
  );
}

// Format a session date range in French, timezone-safe (noon anchor avoids day-shift)
function formatSessionRange(startIso, endIso) {
  const opts = { day: "numeric", month: "long", year: "numeric" };
  const s = new Date(`${startIso}T12:00:00`);
  if (Number.isNaN(s.getTime())) return startIso;
  if (!endIso) return s.toLocaleDateString("fr-FR", opts);
  const e = new Date(`${endIso}T12:00:00`);
  if (Number.isNaN(e.getTime())) return s.toLocaleDateString("fr-FR", opts);
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return `${s.getDate()} → ${e.toLocaleDateString("fr-FR", opts)}`;
  }
  if (s.getFullYear() === e.getFullYear()) {
    return `${s.toLocaleDateString("fr-FR", { day: "numeric", month: "long" })} → ${e.toLocaleDateString("fr-FR", opts)}`;
  }
  return `${s.toLocaleDateString("fr-FR", opts)} → ${e.toLocaleDateString("fr-FR", opts)}`;
}

// ============== DATES DE FORMATION (paramétrées via le dashboard admin) ==============
function FormationDates() {
  const [dates, setDates] = useState(null); // null = loading

  useEffect(() => {
    axios.get(`${API}/formation-dates`)
      .then(({ data }) => setDates(data.dates || []))
      .catch(() => setDates([]));
  }, []);

  return (
    <div id="dates" data-testid="section-formation-dates" className="pb-24 sm:pb-32">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <SectionTitle
          eyebrow="Calendrier"
          title="Prochaines sessions"
          lead="Les prochaines dates de la formation d'Énergéticien de la Conscience."
          center
        />
        {dates === null ? (
          <p className="text-center text-sm text-stone dark:text-mist">Chargement…</p>
        ) : dates.length === 0 ? (
          <div data-testid="formation-dates-empty" className="text-center py-12 border border-dashed border-beige dark:border-charcoal rounded-2xl">
            <CalendarDays size={26} className="text-gold mx-auto mb-3" strokeWidth={1.2} />
            <p className="text-stone dark:text-mist italic">Les prochaines dates seront annoncées très prochainement.</p>
          </div>
        ) : (
          <ol data-testid="formation-dates-list" className="border-t border-beige dark:border-charcoal">
            {dates.map((d, i) => (
              <Reveal key={d.id} delay={i * 0.06} y={18}>
                <li data-testid={`formation-date-${d.id}`} className="group flex flex-wrap items-baseline gap-x-8 gap-y-2 border-b border-beige dark:border-charcoal py-6 sm:py-7">
                  <span className="font-serif text-2xl sm:text-3xl text-ink dark:text-whisper sm:min-w-[260px] transition-transform duration-500 group-hover:translate-x-1.5">
                    {formatSessionRange(d.start_date, d.end_date)}
                  </span>
                  <span className="flex-1 text-sm text-stone dark:text-mist">
                    <span className="text-ink dark:text-whisper font-medium">{d.label}</span>
                    {d.location && <span> · {d.location}</span>}
                  </span>
                  <span
                    data-testid={`formation-date-status-${d.id}`}
                    className={`text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 rounded-full ${
                      d.status === "complete"
                        ? "bg-beige dark:bg-charcoal text-stone dark:text-mist"
                        : "bg-forest/10 text-forest dark:bg-gold/15 dark:text-gold"
                    }`}
                  >
                    {d.status === "complete" ? "Complète" : "Ouverte"}
                  </span>
                </li>
              </Reveal>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

// ============== ÉCUSSONS OFFICIELS (galerie Soins Spirituels) ==============
const ECUSSONS = [
  { src: "/badges/badge-certifie.png", testid: "ecusson-certifie", title: "Praticien certifié", line: "La formation initiale validée", target: "statut-certifie" },
  { src: "/badges/badge-agree.png", testid: "ecusson-agree", title: "Praticien agréé", line: "Le réseau officiel", target: "statut-agree" },
  { src: "/badges/badge-maitre.png", testid: "ecusson-maitre", title: "Maître de Soi", line: "Le niveau avancé", target: "statut-maitre" },
];

function SoinsEcussons() {
  return (
    <div data-testid="section-soins-ecussons" className="pb-24 sm:pb-32">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <SectionTitle
          eyebrow="Reconnaissance"
          title="Les écussons officiels"
          lead="Chaque niveau de parcours est honoré par un écusson de l'école, gage d'une pratique reconnue et transmise avec exigence."
          center
        />
        <div className="grid grid-cols-3 gap-4 sm:gap-10 lg:gap-14">
          {ECUSSONS.map((e, i) => (
            <Reveal key={e.testid} delay={i * 0.12} y={30} className="h-full">
              <figure className="h-full">
                <button
                  data-testid={e.testid}
                  onClick={() => smoothScrollTo(`#${e.target}`)}
                  aria-label={`Voir le parcours ${e.title}`}
                  className="group h-full w-full flex flex-col items-center text-center rounded-2xl p-2 -m-2 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
                >
                  <img
                    src={e.src}
                    alt={`Écusson officiel — ${e.title}`}
                    loading="lazy"
                    className="h-24 w-24 sm:h-40 sm:w-40 lg:h-48 lg:w-48 object-contain drop-shadow-[0_18px_30px_rgba(26,33,30,0.22)] transition-transform duration-500 group-hover:scale-105 group-hover:rotate-2"
                  />
                  <span className="mt-5 sm:mt-7 block">
                    <span className="font-serif text-base sm:text-2xl text-ink dark:text-whisper leading-tight block">{e.title}</span>
                    <span className="mt-1.5 text-[10px] sm:text-xs tracking-[0.18em] uppercase text-stone dark:text-mist block">{e.line}</span>
                    <span className="mt-3 inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-forest dark:text-gold opacity-0 translate-y-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0">
                      Découvrir <ArrowRight size={11} />
                    </span>
                  </span>
                </button>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============== FONDATEUR & AUTEUR (page /formation) ==============
function Fondateur() {
  return (
    <div id="fondateur" data-testid="section-fondateur" className="pb-24 sm:pb-32">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[minmax(260px,340px)_1fr] gap-12 lg:gap-16 items-start">
        <Reveal className="relative mx-auto w-full max-w-sm lg:sticky lg:top-28">
          <div className="absolute -inset-3 rounded-[2rem] border border-gold/40 dark:border-gold/30 pointer-events-none" />
          <div className="absolute -inset-10 bg-gold/10 dark:bg-gold/15 blur-3xl rounded-full pointer-events-none" />
          <div className="relative rounded-[1.75rem] overflow-hidden border border-beige dark:border-charcoal bg-black shadow-2xl shadow-black/20 dark:shadow-black/60">
            <img
              data-testid="fondateur-portrait"
              src={PORTRAIT_URL}
              alt="Denis Tran, fondateur et enseignant de l'Énergéticien de la Conscience®"
              className="w-full h-auto object-cover"
              loading="lazy"
            />
          </div>
        </Reveal>
        <div>
          <SectionTitle
            eyebrow="Le fondateur"
            title="Denis Tran"
            lead="Fondateur et enseignant de l'Énergéticien de la Conscience® — plus de 26 ans d'expérience dans les pratiques corporelles, énergétiques et traditionnelles."
          />
          <Reveal delay={0.1}>
            <div data-testid="fondateur-bio" className="space-y-5 text-stone dark:text-mist leading-relaxed text-sm sm:text-base -mt-6">
              <p>
                Denis Tran est diplômé d'un Diplôme Universitaire en Santé et Méditation de la Faculté de Médecine de Montpellier et d'un Diplôme Universitaire en Bien-être et Philosophie Chinoise de l'Université Nice-Sophia Antipolis.
              </p>
              <p>
                Enseignant en Qi Gong et Qi Gong médical, il est certifié en Qi Gong médical par le Dr Jerry Alan Johnson (USA), ainsi qu'en Médecine Traditionnelle Chinoise et Qi Gong auprès de Maître Tran Hoai Ngoc (Vietnam).
              </p>
              <p>
                Il est également certifié en massage thaïlandais par Wat Pho (Thaïlande) et massothérapeute avec plus de 26 années de pratique.
              </p>
              <p>
                Depuis 2014, Denis Tran accompagne à distance des milliers de personnes dans le cadre de sa pratique énergétique, développant une méthode fondée sur l'expérience, l'observation et l'expérimentation. Il enseigne le Qi Gong et la Médecine Traditionnelle Chinoise depuis 2000.
              </p>
              <p>
                Il possède également une expertise en Bazi et en astrologie chinoise, notamment à travers sa formation auprès de Joey Yap (Indonésie).
              </p>
              <p>
                Denis Tran est également auteur de « 8 points merveilleux pour tout soigner », publié aux Éditions Leduc, ainsi que de nombreux e-books consacrés au Taoïsme et à la philosophie taoïste, disponibles sur Amazon.
              </p>
              <p data-testid="fondateur-4c" className="font-serif italic text-forest dark:text-gold">
                « Ces années de pratique, d'enseignement et d'accompagnement ont conduit à la structuration de la Méthode des 4C : Corps, Cœur, Cerveau, Conscience. »
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}

function Auteur() {
  return (
    <div data-testid="section-auteur" className="pb-24 sm:pb-32">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <div className="gold-divider mb-14" />
        <div className="grid sm:grid-cols-[220px_1fr] gap-10 sm:gap-12 items-center">
          <Reveal className="mx-auto sm:mx-0">
            <a
              data-testid="auteur-cover-link"
              href="https://www.editionsleduc.com/produit/1924/9791028514990/8-points-merveilleux-pour-tout-soigner"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative block w-44 sm:w-52"
              aria-label="Découvrir le livre 8 points merveilleux pour tout soigner sur le site des Éditions Leduc"
            >
              <div className="absolute -inset-4 bg-gold/15 dark:bg-gold/20 blur-2xl rounded-full pointer-events-none" />
              <img
                data-testid="auteur-cover"
                src="/images/livre-8-points-merveilleux.webp"
                alt="Couverture du livre « 8 points merveilleux pour tout soigner » de Denis Tran, Éditions Leduc"
                loading="lazy"
                className="relative w-full h-auto rounded-r-lg rounded-l-[3px] border-l-4 border-forest/40 dark:border-gold/40 shadow-[0_24px_50px_-16px_rgba(26,33,30,0.45)] transition-transform duration-500 group-hover:-translate-y-2 group-hover:rotate-1"
              />
            </a>
          </Reveal>
          <div className="text-center sm:text-left">
            <SectionTitle eyebrow="Bibliographie" title="Denis Tran — Auteur" />
            <Reveal>
              <p className="text-stone dark:text-mist leading-relaxed text-base sm:text-lg -mt-6">
                Auteur de l'ouvrage « 8 points merveilleux pour tout soigner » aux Éditions Leduc et de nombreux e-books consacrés au Taoïsme, Denis Tran partage depuis plusieurs années ses connaissances à travers ses livres, formations et enseignements.
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="mt-8 flex flex-wrap justify-center sm:justify-start gap-3">
                <a
                  data-testid="auteur-cta-livre"
                  href="https://www.editionsleduc.com/produit/1924/9791028514990/8-points-merveilleux-pour-tout-soigner"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={btnSecondary}
                >
                  Découvrir le livre <ExternalLink size={14} />
                </a>
                <a
                  data-testid="auteur-cta-amazon"
                  href="https://www.amazon.com/dp/B0HJP58YQ8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={btnSecondary}
                >
                  Mes e-books sur Amazon <ExternalLink size={14} />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============== VIDÉOS YOUTUBE (playlist « Soins en direct et à distance ») ==============
// Réutilisable : page /formation (tid="soins") et accueil (tid="landing")
function SoinsVideos({
  tid = "soins",
  sectionClass = "pb-24 sm:pb-32",
  eyebrow = "Vidéos",
  title = "Découvrir l'approche en vidéo",
  lead = "Ma playlist « Soins en direct et à distance » — parcourez les vidéos directement dans le lecteur.",
}) {
  const [embedUrl, setEmbedUrl] = useState(null);

  // Featured video/playlist is admin-configurable (site setting)
  useEffect(() => {
    axios.get(`${API}/settings/youtube`)
      .then(({ data }) => setEmbedUrl(data.embed_url))
      .catch(() => {});
  }, []);

  return (
    <section data-testid={`section-${tid}-videos`} className={sectionClass}>
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <SectionTitle eyebrow={eyebrow} title={title} lead={lead} center />
        <Reveal>
          <div className="relative overflow-hidden rounded-2xl border border-beige dark:border-charcoal bg-black shadow-2xl shadow-black/15 dark:shadow-black/50 aspect-video">
            {embedUrl ? (
              <iframe
                data-testid={`${tid}-youtube-iframe`}
                className="absolute inset-0 w-full h-full"
                src={embedUrl}
                title="Playlist YouTube — Soins en direct et à distance"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                loading="lazy"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-mist/70 text-sm">Chargement de la vidéo…</div>
            )}
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-center text-xs text-stone dark:text-mist italic">
            Cliquez sur l'icône de liste en haut du lecteur pour parcourir toutes les vidéos de la playlist.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

// ============== LA MÉTHODE DES 4C (page /formation) ==============
const METHODE_4C = [
  { n: "01", t: "Corps", d: "Observer les sensations corporelles, la respiration, les tensions et la zone concernée." },
  { n: "02", t: "Cœur", d: "Prendre en compte le vécu émotionnel et la manière dont la personne vit son expérience." },
  { n: "03", t: "Cerveau", d: "Observer les pensées, l'attention, les représentations et les interprétations liées à l'expérience." },
  { n: "04", t: "Conscience", d: "Développer une présence permettant d'observer l'expérience avec davantage de recul et de conscience." },
];

function Methode4C() {
  return (
    <div data-testid="section-methode-4c" className="pb-24 sm:pb-32">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <SectionTitle
          eyebrow="La structure de la méthode"
          title="La Méthode des 4C"
          lead="Quatre dimensions pour apprendre à accompagner l'expérience de la personne."
          center
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {METHODE_4C.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.08} y={24} className="h-full">
              <div
                data-testid={`methode-4c-card-${i}`}
                className="group h-full rounded-2xl border border-beige dark:border-charcoal bg-sand/60 dark:bg-onyx/60 backdrop-blur-sm p-7 sm:p-8 transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1.5 hover:border-gold/60 dark:hover:border-gold/50 hover:shadow-[0_24px_60px_-24px_rgba(45,74,62,0.35)]"
              >
                <p className="text-[10px] tracking-[0.3em] uppercase text-gold mb-3">{c.n}</p>
                <h3 className="font-serif text-xl sm:text-2xl uppercase tracking-wide text-ink dark:text-whisper mb-2.5">{c.t}</h3>
                <p className="text-sm text-stone dark:text-mist leading-relaxed">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p data-testid="methode-4c-closing" className="mt-12 text-center font-serif italic text-xl sm:text-2xl text-forest dark:text-gold">
            Les 4C constituent la grille fondamentale de l'Énergéticien de la Conscience.
          </p>
        </Reveal>
      </div>
    </div>
  );
}

// ============== À PROXIMITÉ OU À DISTANCE (page /formation) ==============
const DISTANCE_POINTS = ["Sans contact", "À proximité", "À distance", "À des milliers de kilomètres"];

function DistanceSection() {
  return (
    <div data-testid="section-distance" className="py-24 sm:py-32">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
        <SectionTitle eyebrow="La pratique" title="À proximité ou à distance" center />
        <Reveal>
          <div className="space-y-5 text-stone dark:text-mist leading-relaxed text-base sm:text-lg">
            <p>La pratique de l'Énergéticien de la Conscience ne repose pas nécessairement sur le contact physique.</p>
            <p>Elle peut être pratiquée à proximité de la personne ou à distance, y compris lorsque le praticien et la personne accompagnée se trouvent dans des villes, pays ou régions très éloignés.</p>
            <p>La méthode s'appuie sur la présence, l'attention, l'intention, l'observation et les outils énergétiques transmis dans la formation.</p>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div data-testid="distance-points" className="mt-12 flex flex-wrap justify-center gap-2.5">
            {DISTANCE_POINTS.map((p) => (
              <span key={p} className="text-[10px] sm:text-[11px] tracking-[0.18em] uppercase border border-gold/50 bg-sand/70 dark:bg-onyx/70 text-forest dark:text-gold rounded-full px-5 py-2.5">
                {p}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  );
}

// ============== DE LA PRÉSENCE À L'ACCOMPAGNEMENT (page /formation) ==============
function PresenceParcours() {
  const ETAPES = ["Devenir son propre guide", "Ressentir et comprendre", "Accompagner l'autre en conscience"];
  return (
    <div data-testid="section-presence-parcours" className="pb-24 sm:pb-32">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
        {ETAPES.map((e, i) => (
          <Reveal key={e} delay={i * 0.12}>
            <p data-testid={`presence-etape-${i}`} className="font-serif text-2xl sm:text-3xl uppercase tracking-[0.08em] text-ink dark:text-whisper">{e}</p>
            {i < ETAPES.length - 1 && <p className="my-4 text-gold text-2xl leading-none" aria-hidden="true">↓</p>}
          </Reveal>
        ))}
        <Reveal delay={0.3}>
          <p data-testid="presence-progression" className="mt-10 text-[11px] sm:text-xs tracking-[0.22em] uppercase text-forest dark:text-gold">
            Ancrage → Perception → Harmonisation → Accompagnement
          </p>
          <p className="mt-4 text-sm text-stone dark:text-mist italic">
            Ce parcours résume la progression pédagogique des quatre journées.
          </p>
        </Reveal>
      </div>
    </div>
  );
}

// ============== LE PROTOCOLE DES 4C (page /formation) ==============
const PROTOCOLE_4C = [
  { t: "Corps", q: "Que se passe-t-il dans le corps ?" },
  { t: "Cœur", q: "Comment la personne vit-elle son expérience ?" },
  { t: "Cerveau", q: "Quelles pensées, représentations ou attentions sont présentes ?" },
  { t: "Conscience", q: "Que peut-elle observer sans chercher immédiatement à modifier ?" },
  { t: "Pratique énergétique", q: "L'Énergéticien utilise les techniques enseignées dans la formation, sans contact physique." },
  { t: "Intégration", q: "La personne décrit elle-même son expérience et les éventuelles évolutions de son ressenti." },
];

function Protocole4C() {
  return (
    <div data-testid="section-protocole-4c" className="pb-24 sm:pb-32">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <SectionTitle eyebrow="En pratique" title="Le protocole des 4C" center />
        <ol className="border-t border-beige dark:border-charcoal">
          {PROTOCOLE_4C.map((p, i) => (
            <Reveal key={p.t} delay={i * 0.05} y={16}>
              <li data-testid={`protocole-step-${i}`} className="grid sm:grid-cols-[220px_1fr] items-baseline gap-x-8 gap-y-1.5 border-b border-beige dark:border-charcoal py-5">
                <span className="font-serif text-xl sm:text-2xl uppercase tracking-wide text-forest dark:text-gold">{p.t}</span>
                <span className="text-sm sm:text-base text-stone dark:text-mist leading-relaxed">{p.q}</span>
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
    </div>
  );
}

// ============== MESSAGE FINAL AVANT CANDIDATURE (page /formation) ==============
function CtaCandidature() {
  return (
    <section data-testid="section-cta-candidature" className="py-24 sm:py-32 bg-ivory/85 dark:bg-night/85">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] bg-forest dark:bg-charcoal px-6 py-14 sm:px-12 sm:py-16 lg:px-16 text-center shadow-[0_40px_120px_-40px_rgba(26,33,30,0.5)]">
            <span aria-hidden="true" className="qi-breath absolute -right-12 -top-20 font-serif leading-none select-none text-[18rem] sm:text-[24rem] text-gold/10 pointer-events-none">氣</span>
            <div className="relative">
              <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-ivory">
                Apprendre à accompagner en conscience
              </h2>
              <p className="mt-6 text-whisper/70 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
                Quatre jours pour découvrir les fondations de la Méthode des 4C, développer votre présence et votre perception énergétique, et apprendre à pratiquer un accompagnement sans contact, à proximité comme à distance.
              </p>
              <div className="mt-10">
                <button
                  data-testid="cta-candidature-btn"
                  onClick={() => smoothScrollTo("#candidature")}
                  className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-[11px] font-semibold tracking-[0.25em] uppercase text-ink transition-[background-color,gap] duration-300 hover:bg-gold-bright hover:gap-3.5"
                >
                  Candidater à la formation <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function SoinsSpirituels({ onDecouvrir }) {
  return (
    <section id="soins-spirituels" data-testid="section-soins-spirituels" className="relative overflow-hidden bg-ivory/85 dark:bg-night/85">
      <SoinsHero onDecouvrir={onDecouvrir} />
      <Methode4C />
      <NotreApproche />
      <DistanceSection />
      <QiMarquee />
      <ParcoursFormation onDecouvrir={onDecouvrir} />
      <PresenceParcours />
      <Protocole4C />
      <Fondateur />
      <FormationDates />
      <Auteur />
      <SoinsVideos />
      <SoinsEcussons />
      <SoinsLegal />
    </section>
  );
}

function Formation() {
  const [selectedIntent, setSelectedIntent] = useState(null);

  const scrollToForm = (intent) => {
    if (intent) setSelectedIntent(intent);
    setTimeout(() => {
      smoothScrollTo("#candidature");
    }, 50);
  };

  return (
    <div data-testid="page-formation">
      <FormationHero />
      <SoinsSpirituels onDecouvrir={() => scrollToForm("formation-initiale")} />
      <FormationInitiale onCandidater={() => scrollToForm("formation-initiale")} />
      <FormationStatuts onSelectIntent={scrollToForm} />
      <FormationAnciensEleves onSelectIntent={scrollToForm} />
      <FormationFormateur onSelectIntent={scrollToForm} />
      <Reviews
        sectionId=""
        tid="formation-"
        eyebrow="Ils l'ont vécu"
        title="Paroles d'élèves & de stagiaires"
        lead="Les mots de celles et ceux qui ont suivi le parcours ou reçu un accompagnement."
        withForm={true}
        bgClass="bg-sand/60 dark:bg-onyx/60"
        emptyText="Les témoignages d'élèves seront bientôt publiés ici."
        formTitle="Partager mon expérience de formation"
        formLead="Vous avez suivi la formation ? Votre témoignage d'élève sera publié sur cette page après vérification."
      />
      <div className="bg-ivory/85 dark:bg-night/85 pt-24 sm:pt-32">
        <SoinsFaq />
      </div>
      <CtaCandidature />
      <FormationCandidatureForm defaultIntent={selectedIntent} />
      <Ressources />
    </div>
  );
}

// ============== LANDING (route "/") ==============
// ============== SECTIONS ACCUEIL (nouveau parcours visiteur) ==============
const PRESENTATION_POINTS = [
  { icon: Heart, text: "26 ans d'expérience dans le massage et les pratiques corporelles" },
  { icon: GraduationCap, text: "D.U. Méditation et Santé — Faculté de Médecine de Montpellier" },
  { icon: GraduationCap, text: "D.U. Bien-être et Philosophie chinoise — Université Nice Sophia Antipolis" },
  { icon: Sparkles, text: "Praticien en Qigong médical (QiGong Liao Fa) et élève du Dr. Jerry Alan Johnson" },
  { icon: BookOpen, text: "Auteur de « 8 points merveilleux pour tout soigner » chez Leduc" },
  { icon: Compass, text: "Auteur de plusieurs ouvrages et ebooks consacrés au Tao et aux pratiques énergétiques" },
];

function Presentation() {
  return (
    <section id="presentation" data-testid="section-presentation" className="py-24 sm:py-32 bg-ivory/85 dark:bg-night/85">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionTitle eyebrow="Présentation" title="Bienvenue dans mon univers" center />
        <Reveal>
          <div className="max-w-3xl mx-auto space-y-5 text-center text-stone dark:text-mist leading-relaxed text-base sm:text-lg">
            <p>
              Je suis Denis Tran, praticien, enseignant et auteur. Depuis plus de 25 ans,
              j'explore les liens entre le corps, l'énergie, la conscience et les traditions orientales.
            </p>
            <p>
              Mon approche s'est construite à partir du massage, du Qi Gong, de la Médecine
              Traditionnelle Chinoise, du Taoïsme, de la méditation et de différentes
              expériences d'accompagnement.
            </p>
            <p>
              Aujourd'hui, j'ai structuré cette expérience dans une approche personnelle :
              l'Énergétique de la Conscience.
            </p>
          </div>
        </Reveal>
        <div className="mt-14 max-w-5xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PRESENTATION_POINTS.map((p, i) => (
            <Reveal key={i} delay={i * 0.06} y={20} className="h-full">
              <div data-testid={`presentation-point-${i}`} className="h-full flex items-start gap-3 rounded-2xl border border-beige dark:border-charcoal bg-sand/60 dark:bg-onyx/60 p-5">
                <p.icon size={20} strokeWidth={1.5} className="text-forest dark:text-gold shrink-0 mt-0.5" />
                <p className="text-sm text-stone dark:text-mist leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <div className="mt-12 text-center">
            <Link data-testid="presentation-cta-parcours" to="/formation#fondateur" className={btnPrimary}>
              Découvrir mon parcours <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ============== LES ACCOMPAGNEMENTS (accueil) ==============
const ACCOMPAGNEMENTS = [
  { icon: Heart, title: "Accompagnements individuels", desc: "Séances personnalisées, notamment à distance, dans le cadre de l'approche énergétique développée par Denis Tran." },
  { icon: Radio, title: "Lives et expériences en direct", desc: "Des lives, démonstrations et expériences proposés sur les réseaux sociaux pour découvrir concrètement l'approche." },
  { icon: Sparkles, title: "Approche énergétique", desc: "Travail autour de l'attention, de la respiration, de la perception corporelle, de la visualisation et de l'énergie." },
];

function Accompagnements() {
  return (
    <section id="accompagnements" data-testid="section-accompagnements" className="py-24 sm:py-32 bg-sand/85 dark:bg-onyx/85">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <SectionTitle eyebrow="Être accompagné" title="Les accompagnements" center />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {ACCOMPAGNEMENTS.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.08} y={24} className="h-full">
              <article data-testid={`accompagnement-card-${i}`} className="group h-full rounded-2xl border border-beige dark:border-charcoal bg-ivory/70 dark:bg-night/70 p-7 sm:p-8 transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1.5 hover:border-gold/60 dark:hover:border-gold/50 hover:shadow-[0_24px_60px_-24px_rgba(45,74,62,0.35)]">
                <a.icon size={26} strokeWidth={1.25} className="text-forest dark:text-gold mb-5 transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6" />
                <h3 className="font-serif text-2xl text-ink dark:text-whisper leading-tight">{a.title}</h3>
                <p className="mt-4 text-sm text-stone dark:text-mist leading-relaxed">{a.desc}</p>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <div className="mt-12 text-center">
            <a data-testid="accompagnements-cta-rdv" href="https://tidycal.com/denistran" target="_blank" rel="noopener noreferrer" className={btnPrimary}>
              Prendre rendez-vous <ArrowRight size={14} />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ============== FORMATION (accueil) ==============
const FORMATION_POINTS = [
  "Pratique énergétique", "Conscience corporelle", "Respiration et attention",
  "Perception et présence", "Relation praticien / personne accompagnée",
  "Recueil du consentement", "Confidentialité", "Cadre professionnel",
  "Limites de la pratique",
];

function FormationTeaser() {
  return (
    <section id="formation-teaser" data-testid="section-formation-teaser" className="py-24 sm:py-32 bg-sand/85 dark:bg-onyx/85">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
        <SectionTitle
          eyebrow="Devenir praticien"
          title="Formation Énergéticien de la Conscience"
          lead="Une formation destinée aux personnes qui souhaitent approfondir leur compréhension des pratiques énergétiques et apprendre à accompagner autrui dans un cadre structuré."
          center
        />
        <Reveal>
          <h3 className="font-serif text-2xl sm:text-3xl text-forest dark:text-gold">
            Praticien Certifié en Énergétique de la Conscience
          </h3>
        </Reveal>
        <Reveal delay={0.1}>
          <div data-testid="formation-teaser-points" className="mt-8 flex flex-wrap justify-center gap-2.5">
            {FORMATION_POINTS.map((p) => (
              <span key={p} className="text-[10px] sm:text-[11px] tracking-[0.14em] uppercase border border-beige dark:border-charcoal bg-sand/70 dark:bg-onyx/70 text-stone dark:text-mist rounded-full px-4 py-2">
                {p}
              </span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.18}>
          <div className="mt-12">
            <Link data-testid="formation-teaser-cta" to="/formation" className={btnPrimary}>
              Découvrir la formation <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ============== ENSEIGNEMENTS (accueil) ==============
const ENSEIGNEMENTS = [
  { icon: Compass, title: "Le Tao", desc: "Philosophie, conscience et transformation intérieure." },
  { icon: Sparkles, title: "Qi Gong et pratiques énergétiques", desc: "Explorer le corps, la respiration, l'attention et l'énergie." },
  { icon: BookOpen, title: "Livres et ebooks", desc: "Retrouvez mes ouvrages et publications." },
];

function Enseignements() {
  return (
    <section id="enseignements" data-testid="section-enseignements" className="py-24 sm:py-32 bg-sand/85 dark:bg-onyx/85">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <SectionTitle eyebrow="Expérimenter et apprendre" title="Mes enseignements" center />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {ENSEIGNEMENTS.map((e, i) => (
            <Reveal key={e.title} delay={i * 0.08} y={24} className="h-full">
              <article data-testid={`enseignement-card-${i}`} className="group h-full rounded-2xl border border-beige dark:border-charcoal bg-ivory/70 dark:bg-night/70 p-7 sm:p-8 transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1.5 hover:border-gold/60 dark:hover:border-gold/50 hover:shadow-[0_24px_60px_-24px_rgba(45,74,62,0.35)]">
                <e.icon size={26} strokeWidth={1.25} className="text-forest dark:text-gold mb-5 transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6" />
                <h3 className="font-serif text-2xl text-ink dark:text-whisper leading-tight">{e.title}</h3>
                <p className="mt-4 text-sm text-stone dark:text-mist leading-relaxed">{e.desc}</p>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <div className="mt-12 text-center">
            <Link data-testid="enseignements-cta" to="/ressources" className={btnPrimary}>
              Voir tous les enseignements <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ============== CADRE PROFESSIONNEL (accueil) ==============
function CadrePro() {
  return (
    <section id="cadre-professionnel" data-testid="section-cadre-pro" className="py-24 sm:py-32 bg-sand/60 dark:bg-onyx/60">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
        <SectionTitle eyebrow="Cadre professionnel" title="Un accompagnement dans un cadre clair" center />
        <Reveal>
          <div className="space-y-5 text-stone dark:text-mist leading-relaxed text-base sm:text-lg">
            <p>Les pratiques proposées sur ce site s'inscrivent dans une démarche complémentaire et de bien-être.</p>
            <p>Elles ne constituent pas des actes médicaux et ne remplacent ni un diagnostic, ni un traitement, ni un suivi médical.</p>
            <p>Le consentement, la confidentialité, le respect de la personne et sa liberté de décision sont essentiels dans chaque accompagnement.</p>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="mt-10">
            <a data-testid="cadre-cta-ethique" href="#declaration" className={btnSecondary}>
              Voir le cadre éthique et professionnel <ArrowRight size={14} />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ============== APPEL À L'ACTION FINAL (accueil) ==============
function CtaFinal() {
  return (
    <section id="prochaine-etape" data-testid="section-cta-final" className="py-24 sm:py-32 bg-ivory/85 dark:bg-night/85">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] bg-forest dark:bg-charcoal px-6 py-14 sm:px-12 sm:py-16 lg:px-16 text-center shadow-[0_40px_120px_-40px_rgba(26,33,30,0.5)]">
            <span aria-hidden="true" className="qi-breath absolute -right-12 -top-20 font-serif leading-none select-none text-[18rem] sm:text-[24rem] text-gold/10 pointer-events-none">氣</span>
            <div className="relative">
              <div className="flex items-center justify-center gap-3 mb-8">
                <span className="h-px w-10 bg-gold" />
                <span className="text-[11px] tracking-[0.35em] uppercase text-gold">Par où commencer</span>
                <span className="h-px w-10 bg-gold" />
              </div>
              <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-ivory">
                Quelle sera votre prochaine étape ?
              </h2>
              <p className="mt-5 text-whisper/70 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
                Découvrez mon approche, expérimentez-la ou apprenez à la pratiquer.
              </p>
              <div className="mt-10 flex flex-wrap justify-center gap-3">
                <Link data-testid="cta-final-accompagne" to="/soins" className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-[11px] font-semibold tracking-[0.25em] uppercase text-ink transition-[background-color,gap] duration-300 hover:bg-gold-bright hover:gap-3.5">
                  Être accompagné <ArrowRight size={14} />
                </Link>
                <Link data-testid="cta-final-methode" to="/energetique-de-la-conscience" className="inline-flex items-center gap-2 rounded-full border border-ivory/30 px-8 py-4 text-[11px] font-semibold tracking-[0.25em] uppercase text-ivory transition-colors duration-300 hover:border-gold hover:text-gold">
                  Découvrir l'Énergétique de la Conscience
                </Link>
                <Link data-testid="cta-final-praticien" to="/formation" className="inline-flex items-center gap-2 rounded-full border border-ivory/30 px-8 py-4 text-[11px] font-semibold tracking-[0.25em] uppercase text-ivory transition-colors duration-300 hover:border-gold hover:text-gold">
                  Devenir praticien
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ---- /contact : accès rapide, FAQ et formulaire ----
function ContactPage({ onChatOpen }) {
  return (
    <div data-testid="page-contact">
      <PageHero eyebrow="Contact" title="Parlons de votre démarche" subtitle="Une question, une demande, un projet ? Écrivez-moi." />
      <QuickAccess onChatOpen={onChatOpen} />
      <Faq />
      <Contact />
    </div>
  );
}

// ---- /temoignages : tous les témoignages authentiques ----
function TemoignagesPage() {
  return (
    <div data-testid="page-temoignages">
      <PageHero eyebrow="Témoignages" title="Ils partagent leur expérience" subtitle="Des témoignages authentiques, lus et vérifiés avant publication." />
      <Reviews
        sectionId=""
        tid="temoignages-"
        eyebrow="Tous les témoignages"
        title="Témoignages & ressentis"
        lead="Chaque avis est lu et vérifié avant d'être publié. Vous pouvez à votre tour partager votre expérience."
        grid
        bgClass="bg-ivory/85 dark:bg-night/85"
        formTitle="Partager mon expérience"
        formLead="Votre témoignage sera visible après vérification."
      />
    </div>
  );
}

// ============== BADGE TAO DES 4C (accueil — visibilité de l'univers, juste après le hero) ==============
function Tao4CBadge() {
  return (
    <section data-testid="section-tao4c-badge" className="relative py-10 sm:py-14 bg-ivory/85 dark:bg-night/85">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Link
            to="/tao-des-4c"
            data-testid="tao4c-badge-card"
            className="group flex flex-col sm:flex-row items-center gap-6 sm:gap-8 rounded-2xl border border-beige dark:border-charcoal bg-sand/60 dark:bg-onyx/60 px-7 py-6 sm:px-9 transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1 hover:border-gold/60 dark:hover:border-gold/50 hover:shadow-[0_24px_60px_-24px_rgba(45,74,62,0.35)]"
          >
            <img
              src="/images/tao-des-4c-logo.webp"
              alt="Le Tao des 4C — Corps, Cœur, Cerveau, Conscience"
              data-testid="tao4c-badge-logo"
              className="h-28 w-28 sm:h-32 sm:w-32 object-contain transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            <div className="text-center sm:text-left flex-1">
              <p className="text-[10px] tracking-[0.3em] uppercase text-gold">Univers complémentaire</p>
              <p className="mt-1.5 font-serif text-2xl sm:text-3xl text-ink dark:text-whisper">Le Tao des 4C</p>
              <p className="mt-2 text-sm text-stone dark:text-mist leading-relaxed">
                Corps • Cœur • Cerveau • Conscience — carnets, jeu et expériences interactives pour explorer votre propre chemin.
              </p>
            </div>
            <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.16em] uppercase font-medium text-forest dark:text-gold whitespace-nowrap">
              Découvrir <ArrowRight size={12} className="transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function Landing() {
  return (
    <>
      <Hero />
      <Tao4CBadge />
      {/* ============== ADSENSE: Auto Ads enabled via head script (ca-pub-7249813540595739) ============== */}
      <Presentation />
      <Methode />
      <Parcours />
      <Accompagnements />
      <FormationTeaser />
      <MesUnivers />
      <TourneeTeaser />
      <Socials />
      <Enseignements />
      <Reviews withForm={false} title="Ils partagent leur expérience" allHref="/temoignages" />
      <CadrePro />
      <Declaration />
      <CtaFinal />
    </>
  );
}

// ============== ROUTES WRAPPER ==============
function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    // Update <title> per route (light SEO)
    const TITLES = {
      "/": "Le Guérisseur — Denis Tran | Corps, Énergie, Conscience et Tao",
      "/soins": "Soins individuels à distance | Le Guérisseur",
      "/energetique-de-la-conscience": "Énergétique de la Conscience | Denis Tran",
      "/formation": "Formation énergétique 4 jours | Énergéticien de la Conscience® — Denis Tran",
      "/formation-energetique-de-la-conscience": "Formation Énergétique de la Conscience | Denis Tran",
      "/ressources": "Ressources complémentaires | Le Guérisseur — Denis Tran",
      "/tao-des-4c": "Le Tao des 4C | Corps, Cœur, Cerveau, Conscience",
      "/reseaux-sociaux": "Le Guérisseur | Retrouvez Denis Tran sur les réseaux sociaux",
      "/contact": "Contact | Le Guérisseur — Denis Tran",
      "/temoignages": "Témoignages | Le Guérisseur — Denis Tran",
      "/tournee": "La Tournée Le Guérisseur — Soins, Rencontres & Formation",
    };
    document.title = TITLES[pathname] || TITLES["/"];
    // Restore scroll on route change (unless there's an anchor)
    if (!hash) {
      if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
      else window.scrollTo({ top: 0, behavior: "auto" });
    } else {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => smoothScrollTo(el), 60);
    }
  }, [pathname, hash]);
  return null;
}

// ============== APP ==============
function AppShell() {
  const [theme, setTheme] = useTheme();
  const [adminOpen, setAdminOpen] = useState(() => typeof window !== "undefined" && new URLSearchParams(window.location.search).has("admin"));
  const [chatOpen, setChatOpen] = useState(false);
  const openChat = () => setChatOpen(true);
  const location = useLocation();
  // La tournée (/tournee) a ses propres header/footer et n'apparaît pas dans le menu principal
  const isTournee = location.pathname.startsWith("/tournee");

  // Lenis: smooth momentum scrolling + route all in-page anchors through it
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => { lenis.raf(time); rafId = requestAnimationFrame(raf); };
    rafId = requestAnimationFrame(raf);
    const onClick = (e) => {
      const a = e.target.closest?.('a[href^="#"]');
      if (!a) return;
      const hash = a.getAttribute("href");
      if (!hash || hash.length < 2) return;
      const el = document.querySelector(hash);
      if (!el) return;
      e.preventDefault();
      smoothScrollTo(el);
    };
    document.addEventListener("click", onClick);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      document.removeEventListener("click", onClick);
      delete window.__lenis;
    };
  }, []);

  return (
    <div className="App text-ink dark:text-whisper min-h-screen relative">
      {/* ============== Atmospheric background: misty Chinese mountains with temple applied
          via body background-image in index.css (fixed, cover, with a soft veil overlay). ============== */}
      <ScrollManager />
      {!isTournee && <Nav theme={theme} setTheme={setTheme} onAdminClick={() => setAdminOpen(true)} />}
      <main className="relative">
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/soins" element={<SoinsPage />} />
          <Route path="/energetique-de-la-conscience" element={<EnergetiquePage />} />
          <Route path="/formation" element={<Formation />} />
          <Route path="/formation-energetique-de-la-conscience" element={<Formation />} />
          <Route path="/la-voie-libre-du-tao" element={<Navigate to="/ressources" replace />} />
          <Route path="/ressources" element={<RessourcesPage />} />
          <Route path="/tao-des-4c" element={<TaoQuatreCPage />} />
          <Route path="/reseaux-sociaux" element={<ReseauxSociauxPage />} />
          <Route path="/contact" element={<ContactPage onChatOpen={openChat} />} />
          <Route path="/temoignages" element={<TemoignagesPage />} />
          <Route path="/tournee" element={<TourneeLanding />} />
          <Route path="/tournee/admin" element={<TourneeAdmin />} />
          <Route path="/tournee/:slug" element={<TourneeVille />} />
          <Route path="*" element={<Landing />} />
        </Routes>
      </main>
      {!isTournee && <Footer onAdminClick={() => setAdminOpen(true)} />}
      {!isTournee && <EventBanner />}
      {!isTournee && <ChatFab onClick={openChat} />}
      {!isTournee && <ChatWidget open={chatOpen} onClose={() => setChatOpen(false)} />}
      <AdminPanel open={adminOpen} onClose={() => setAdminOpen(false)} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}

export default App;
